export const manualData = [
  {
    category: "Superadmin / Admin",
    sections: [
      {
        section: "Admin Menu",
        topics: [
          {
            id: 1,
            title: "Create/Edit Student",
            description: `
<strong>Creating a New Student</strong>

To create a new student account in the system, follow these steps:

<strong>1. Access the Create Student Form</strong>
- Click the <strong>"Create New Student"</strong> button from the Students management page
- A form will appear with multiple sections to fill out

<strong>2. Select Branch (Superadmin Only)</strong>
- If you're a <strong>Superadmin</strong>, select the branch from the dropdown
- For <strong>Admin users</strong>, the branch is automatically set to your assigned branch

<strong>3. Fill in Basic Information</strong>
- <strong>First Name</strong>: Student's legal first name (required)
- <strong>Last Name</strong>: Student's legal last name (required)
- <strong>Nickname</strong>: Preferred name for daily use (required)
- <strong>Gender</strong>: Select Male or Female (optional)
- <strong>National ID</strong>: Student's identification number (optional)
- <strong>Date of Birth</strong>: Select from date picker (required)

<strong>4. Account Credentials</strong>
- <strong>Email</strong>: Must be a valid email format (required) - used for login
- <strong>Temporary Password</strong>: Set an initial password (required)
  - Click <strong>"Generate Password"</strong> to auto-create a secure password
  - Remember to share this with the student/guardian

<strong>5. Contact Information</strong>
- <strong>Phone</strong>: Student's contact number (optional)
- <strong>Address</strong>: Full address in text area (optional)

<strong>6. Education Details</strong>
- <strong>Current Education Level</strong>: Choose from:
  - Year 1-12 (for year-based systems)
  - Grade 1-12 (for grade-based systems)
  - University or Adults
- <strong>School Name</strong>: Name of current school (optional)

<strong>7. Guardian Information</strong>
- <strong>Parent/Guardian Name</strong>: Full name of guardian (optional)
- <strong>Parent/Guardian Phone</strong>: Contact number (optional)

<strong>8. Submit the Form</strong>
- Click <strong>"Create Student"</strong> button
- Review the confirmation modal
- Click <strong>"Confirm"</strong> to finalize creation

<strong>Viewing & Managing Students</strong>


<strong>Editing a Student</strong>
1. Find the student in the table
2. Click the <strong>Edit</strong> icon in the Actions column
3. Modify the necessary information in the modal
4. Click <strong>"Save"</strong> to update

<strong>Managing Student Status</strong>
- Use the toggle switch in the Status column to activate/deactivate students
- Active students can log in and attend classes
- Inactive students cannot access the system

<strong>Tips</strong>
- Always generate a strong password for security
- Keep guardian contact information updated
- Use the refresh button to see the latest data
- Export student lists for record-keeping
`,
            image: "/manual-images/createstudent.png",
          },
          {
  id: 2,
  title: "Create/Edit Teacher",
  description: `
<strong>Creating a New Teacher</strong>

To add a new teacher to the system, follow these steps:

<strong>1. Access the Create Teacher Form</strong>
- Navigate to <strong>Admin Menu → Teachers</strong>
- Click the <strong>"Create New Teacher"</strong> button
- A form modal will appear

<strong>2. Upload Profile Photo</strong>
- Click on the avatar/upload area at the top of the form
- Select a photo from your computer
- Supported formats: JPG, PNG (recommended size: 400x400px)
- The photo will be displayed as a circular avatar

<strong>3. Fill in Basic Information</strong>
- <strong>First Name</strong>: Teacher's legal first name (required)
- <strong>Last Name</strong>: Teacher's legal last name (required)
- <strong>Nickname</strong>: Preferred name for daily use (required)
- <strong>Email</strong>: Must be valid email format (required) - used for login
- <strong>Phone</strong>: Contact number (optional)
- <strong>National ID</strong>: Identification number (optional)
- <strong>Address</strong>: Full address in text area (optional)

<strong>4. Enter Academic Details</strong>

<strong>Bachelor's Degree:</strong>
- <strong>Degree Name</strong>: e.g., "B.A. in English Literature"
- <strong>School/University</strong>: Institution name

<strong>Master's Degree:</strong> (optional)
- <strong>Degree Name</strong>: e.g., "M.Ed. in TESOL"
- <strong>School/University</strong>: Institution name

<strong>Doctoral Degree:</strong> (optional)
- <strong>Degree Name</strong>: e.g., "Ph.D. in Education"
- <strong>School/University</strong>: Institution name

<strong>5. Teaching Specialization</strong>
- <strong>Specialized Subjects</strong>: List subjects the teacher can teach
  - Example: "Mathematics, Physics, Chemistry"
  - Separate multiple subjects with commas
  
<strong>6. Additional Information</strong>
- <strong>About/Schedule</strong>: Brief description or availability notes
  - Example: "Available Mon-Fri, specializes in SAT prep"
- <strong>Languages</strong>: Select all languages the teacher speaks
  - Choose from: Thai, English (can select multiple)

<strong>7. Save Teacher Profile</strong>
- Click <strong>"Save"</strong> to create the teacher account
- Click <strong>"Cancel"</strong> to discard changes

<strong>Editing an Existing Teacher</strong>

1. Go to the Teachers list in Admin Menu
2. Click the <strong>Edit</strong> icon next to the teacher's name
3. The edit modal will open with current information pre-filled
4. Modify any fields as needed
5. Click <strong>"Save"</strong> to update

<strong>Managing Teacher Photo</strong>
- To change photo: Click on the current avatar and select a new image
- To remove photo: Upload process will replace the old photo automatically
- Photos are stored securely and displayed throughout the system
`,
  image: "/manual-images/createteacher.png",
},
          {
  id: 3,
  title: "Create/Edit Subjects",
  description: `
<strong>Creating a New Subject</strong>

Subjects are the courses that teachers teach and students enroll in. Follow these steps to create a subject:

<strong>1. Access the Subject Creation Form</strong>
- Navigate to <strong>Admin Menu → Subjects</strong>
- Click the <strong>"Create Subject"</strong> button
- A modal form will appear

<strong>2. Select Subject Group</strong>
- <strong>Subject Group</strong>: Choose the category this subject belongs to (required)
  - Example groups: Mathematics, Sciences, Languages, Test Prep, etc.
  - You can search by typing in the dropdown
  - If the group doesn't exist, create it first in Subject Groups management

<strong>3. Choose Education Level</strong>
- <strong>Level</strong>: Select the appropriate education level (required)
  
<strong>Available Level Options:</strong>
- <strong>US Grades</strong>: G. 1 through G. 13
- <strong>UK Years</strong>: Y. 1 through Y. 12
- <strong>General Levels</strong>: Beginner, Intermediate, Advanced, College, General

<strong>4. Select or Type Subject Title</strong>
- <strong>Subject Title</strong>: Choose from predefined options or type your own (required)
- You can either select from the dropdown or type a custom title

<strong>Predefined Title Categories:</strong>

<strong>UK Curriculum:</strong>
- KS1, KS2, KS3, IGCSE, A-Level
- CAT4, UKiSet, ISEB P, ISEB C
- CP1, CP2, C3

<strong>US Curriculum:</strong>
- Improve School Grades, GED
- PSAT, SAT, SSAT, AP, ACT, MAP Test

<strong>University Entrance:</strong>
- KMIDS, MUIDS, SPI
- PCU-ATS, CU-AAT
- GMAT, GRE

<strong>English Proficiency:</strong>
- IELTS, TOEFL (Primary, Jr, ITP, IiBT)
- Essay Writing, Conversation
- Business English, Thai Language

<strong>IB Curriculum:</strong>
- IB PYP, IB MYP, IB Diploma

<strong>5. Select Specification</strong>
- <strong>Specification</strong>: Choose the specific subject area (required)
- This field appears after selecting a title
- Different specifications appear based on the selected title

<strong>General Specifications</strong> (for most subjects):
- English, Math, Science
- Biology, Physics, Chemistry
- Verbal, Non-Verbal
- Further Math, Pure Math, Calculus
- Business Studies, Economics
- EFL, ESL, History, Geography
- Essay Writing

<strong>English Proficiency Specifications</strong> (for language tests):
- Speaking, Listening, Writing, Reading
- Language Form & Meaning
- Structure & Written Expression

<strong>6. Add Description</strong>
- <strong>Description</strong>: Brief explanation of the subject (required)
  - Maximum 255 characters
  - Character counter is displayed
  - Be clear and concise about what the subject covers
  - Example: "Advanced preparation for SAT Math covering algebra, geometry, and data analysis"

<strong>7. Add Image URL (Optional)</strong>
- <strong>Image URL</strong>: Link to a subject icon or image
  - Optional field
  - Use for visual identification in course listings

<strong>8. Save the Subject</strong>
- Click <strong>"Save"</strong> to create the subject
- Click <strong>"Cancel"</strong> to discard changes
- Success message will appear when saved

<strong>How Subject Names are Constructed</strong>

The final subject name combines multiple fields:
- <strong>Format</strong>: [Title] - [Specification] ([Level])
- <strong>Example</strong>: "SAT - Math (G. 11)"
- <strong>Example</strong>: "IGCSE - Physics (Y. 10)"
- <strong>Example</strong>: "IELTS - Speaking (Advanced)"

<strong>Editing an Existing Subject</strong>

1. Go to the Subjects list in Admin Menu
2. Click the <strong>Edit</strong> icon next to the subject
3. The modal will open with current information
4. Modify any fields as needed
5. Click <strong>"Save"</strong> to update
`,
  image: "/manual-images/createsubject.png",
},
         {
  id: 4,
  title: "Create/Edit Classrooms",
  description: `
<strong>Creating a New Classroom</strong>

Classrooms are physical or virtual spaces where classes are held. Follow these steps to create a classroom:

<strong>1. Access the Classroom Creation Form</strong>
- Navigate to <strong>Admin Menu → Classrooms</strong>
- Click the <strong>"Create Classroom"</strong> button
- A modal form will appear

<strong>2. Fill in Classroom Details</strong>

<strong>Room Number</strong>
- Enter the classroom identifier (required)
- Examples: "Room 101", "A-205", "Online Room 1"
- Use a consistent naming convention
- Can be alphanumeric (letters and numbers)

<strong>Capacity</strong>
- Enter the maximum number of students the room can hold (required)
- Must be a number
- Examples: 10, 15, 20, 30
- Consider physical space and comfort
- For online rooms, set based on optimal class size

<strong>Availability Status</strong>
- Select the current availability of the classroom (required)
- <strong>Available</strong>: Room can be assigned to classes
- <strong>Not Available</strong>: Room is closed or under maintenance
- Change this status anytime to enable/disable room usage

<strong>3. Save the Classroom</strong>
- Click <strong>"Save"</strong> to create the classroom
- The modal will close automatically
- The new classroom will appear in the list

<strong>Editing an Existing Classroom</strong>

1. Go to the Classrooms list in Admin Menu
2. Click the <strong>Edit</strong> icon next to the classroom
3. The modal will open with current information pre-filled
4. Modify any fields as needed:
   - Update room number if relocated
   - Adjust capacity if room layout changes
   - Change availability status for maintenance
5. Click <strong>"Save"</strong> to update

<strong>Classroom Usage</strong>

Once created, classrooms can be:
- Assigned to scheduled classes
- Filtered by availability when creating schedules
- Viewed in the branch schedule
- Used for capacity planning

<strong>Best Practices</strong>

<strong>Naming Conventions:</strong>
- Use consistent formats (e.g., "Room 101", "A-205")
- Include floor or building if applicable
- Clearly distinguish online vs physical rooms
- Examples: "101", "Building A - Room 5", "Zoom Room 1"


`,
  image: "/manual-images/createclassroom.png",
},
         {
  id: 5,
  title: "Create Course (Pattern)",
  description: `
<strong>Understanding Pattern-Based Courses</strong>

Pattern courses are recurring classes with predictable schedules (daily, weekly, bi-weekly). This is the primary method for creating regular courses in the system.

<strong>1. Access Course Creation</strong>
- Navigate to <strong>Admin Menu → Create Course (Pattern)</strong>
- The form and teacher calendar view will appear side by side
- Use the <strong>Refresh</strong> button to clear the form and reload data

<strong>2. Select Branch</strong>
- <strong>Superadmin</strong>: Choose the branch from dropdown (required)
- <strong>Admin</strong>: Branch is automatically set to your assigned branch

<strong>3. Enter Course</strong>
- <strong>Course</strong>: Unique identifier for the course (required)
- Examples: "SAT-MATH-101", "IGCSE-PHY-A", "IELTS-SPK-001"
- If private class, use Student's Name in the course for better search

<strong>4. Add Subject and Teacher</strong>

For each subject-teacher combination in the course:

<strong>Select Subject:</strong>
- Click the <strong>Subject</strong> dropdown
- Search or scroll to find the subject
- Subject format: [Title] - [Specification] ([Level])
- Example: "SAT - Math (G. 11)"

<strong>Select Teacher:</strong>
- Click the <strong>Teacher</strong> dropdown
- Search by teacher nickname
- Teacher's calendar automatically appears on the right
- Use calendar to check availability and avoid conflicts

<strong>5. Create Schedule Time Slots</strong>

Click <strong>"Add Time Slot"</strong> to add each class session:

<strong>Day</strong> (required):
- Select: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday

<strong>Time</strong> (required):
- Enter start time (6:00 AM - 9:00 PM)
- Use 24-hour format

<strong>Hours & Minutes</strong> (required):
- <strong>Hours</strong>: Select 0, 1, 2, or 3
- <strong>Minutes</strong>: Select 0, 15, 30, or 45
- Example: 1 hour 30 minutes = 1 hour + 30 minutes

<strong>Recurring Pattern</strong> (required):
- <strong>One Class</strong>: Single session only
- <strong>Daily</strong>: Repeats every day
- <strong>Weekly</strong>: Repeats every week on the same day
- <strong>Bi-Weekly</strong>: Repeats every 2 weeks

<strong>Number of Recurrences</strong> (required):
- How many times the class repeats
- Disabled if "One Class" is selected (automatically set to 1)
- Example: 10 weeks = 10 recurrences for weekly pattern

<strong>Start Date</strong> (required):
- Select the first class date
- Format displays as: dd/mm/yy
- Click the field to open date picker

<strong>Delete Time Slot</strong>:
- Click the <strong>trash icon</strong> to remove a time slot

<strong>6. Add Multiple Subjects/Teachers</strong>

Click <strong>"Add Another Subject/Teacher"</strong> to create multi-subject courses:
- Example: SAT course with both Math and English
- Each subject can have different teachers
- Each combination can have multiple time slots
- Delete entire subject-teacher blocks with trash icon

<strong>7. Review Auto-Calculated Fields</strong>

The system automatically calculates:

<strong>Start Date (AUTO)</strong>:
- Earliest date from all time slots
- Updates automatically when you add/modify schedules

<strong>End Date (AUTO)</strong>:
- Latest date based on recurring pattern
- Calculated from: start date + (recurrences × pattern)

<strong>Total Hours (AUTO)</strong>:
- Sum of all class sessions
- Formula: (hours + minutes/60) × recurrences × number_of_slots
- Example: 1.5 hours × 10 weeks = 15 total hours

<strong>8. Add Course Details</strong>

<strong>Notes</strong> (optional):
- Book details, materials needed
- Special instructions
- Prerequisites

<strong>Price</strong> (required):
- Course fee in local currency
- Must be 0 or greater

<strong>Register Capacity</strong> (required):
- Maximum students allowed
- Minimum value: 1

<strong>9. Set Course Type</strong>

<strong>Private Class Checkbox</strong>:
- ☑ <strong>Checked</strong>: Private/Individual class (1-on-1 or small group)
- ☐ <strong>Unchecked</strong>: Group class (larger enrollment)

<strong>10. Use Teacher Calendar</strong>

The calendar on the right shows:
- <strong>Blue events</strong>: Teacher's existing classes
- <strong>Green events</strong>: Teacher's personal schedule/blocked times
- Check for conflicts before scheduling
- Calendar updates when you select different teachers

<strong>11. Submit Course</strong>

Click <strong>"Create Course"</strong>:

<strong>If Schedule Conflicts Exist</strong>:
- Warning popup lists all overlapping dates/times
- Shows: Teacher name, date, time range
- Options:
  - <strong>"Yes, proceed"</strong>: Create despite conflicts
  - <strong>"No, I'll revise"</strong>: Go back and adjust

<strong>If No Conflicts</strong>:
- Confirmation table appears
- Shows all class dates sorted chronologically
- Displays: Date, Time, Teacher, Subject for each session
- Click <strong>"Confirm"</strong> to finalize
- Click <strong>X</strong> to cancel

<strong>Success</strong>:
- "Class created successfully" message appears
- Form clears automatically
- Course is now in the system

<strong>Pattern Examples</strong>

<strong>Example 1: Weekly Math Class</strong>
- Subject: SAT - Math (G. 11)
- Teacher: John
- Day: Tuesday
- Time: 16:00
- Duration: 2 hours 0 minutes
- Recurring: Weekly
- Recurrences: 12
- Result: 12 Tuesday classes, 24 total hours

<strong>Example 2: Multi-Subject Course</strong>
- Subject 1: IGCSE - Physics (Y. 10), Teacher: Sarah
  - Monday 14:00, 1.5 hrs, Weekly, 10 times
- Subject 2: IGCSE - Chemistry (Y. 10), Teacher: Mike
  - Wednesday 14:00, 1.5 hrs, Weekly, 10 times
- Result: 20 total classes, 30 total hours

<strong>Example 3: Intensive Daily Course</strong>
- Subject: IELTS - Speaking (Advanced)
- Teacher: Emma
- Day: Monday-Friday (5 time slots)
- Time: 10:00 each day
- Duration: 1 hour 30 minutes
- Recurring: Daily
- Recurrences: 5
- Result: 25 classes over 5 weeks

<strong>Best Practices</strong>

<strong>Scheduling:</strong>
- Always check teacher calendar before adding slots
- Allow 15-30 minute breaks between classes
- Consider teacher travel time for different locations
- Avoid scheduling during holidays

<strong>Course Structure:</strong>
- Use clear, consistent course name
- Group related subjects in same course
- Match teacher expertise with subject level
- Set realistic register capacity

<strong>Time Management:</strong>
- Use bi-weekly for lighter schedules
- Use weekly for standard courses
- Use daily for intensive programs
- One class option for trial/makeup sessions

<strong>Troubleshooting</strong>

<strong>Calendar not showing?</strong>
- Ensure teacher is selected
- Refresh the page
- Check teacher has schedule configured

<strong>Overlap warnings?</strong>
- Review all conflict dates carefully
- Check if it's acceptable (different students)
- Adjust time slots if necessary
- Contact teacher to confirm availability

<strong>Total hours incorrect?</strong>
- Verify hours and minutes for each slot
- Check number of recurrences
- Ensure all slots have start dates

<strong>Can't submit form?</strong>
- Fill all required fields (marked with red *)
- Ensure dates are valid
- Check that capacity and price are positive numbers
- Verify branch is selected (Superadmin)

<strong>Tips</strong>
- Use the Refresh button to start over cleanly
- Save successful course for reference
- Check teacher calendar throughout the process
- Create templates for recurring course patterns
- Communicate schedule to teachers before finalizing
- Consider time zones if applicable
- Review confirmation table carefully before final submit
`,
  image: "/manual-images/createcourse-pattern.png",
},
       {
  id: 6,
  title: "Create Course (Random)",
  description: `
<strong>Understanding Random/Custom Courses</strong>

Random courses allow you to create classes with flexible, non-recurring schedules. Perfect for makeup classes, irregular schedules, or custom arrangements that don't follow weekly patterns.

<strong>Key Difference from Pattern Courses</strong>
- <strong>Pattern Course</strong>: Uses recurring schedules (weekly, daily, bi-weekly)
- <strong>Random Course</strong>: Each class date/time is specified individually
- Use Random when: Schedule is irregular, one-time events, makeup sessions, or flexible arrangements

<strong>1. Access Random Course Creation</strong>
- Navigate to <strong>Admin Menu → Create Course (Random)</strong>
- Split-screen view: Form on left, Teacher calendar on right
- Click <strong>Refresh</strong> to clear form and reload data

<strong>2. Select Branch</strong>
- <strong>Superadmin</strong>: Choose branch from dropdown (required)
- <strong>Admin</strong>: Branch automatically set to your assigned branch (cannot change)

<strong>3. Enter Course Code</strong>
- <strong>Course Code</strong>: Unique identifier (required)
- Examples: "MAKEUP-SAT-001", "CUSTOM-IELTS-JAN", "PRIVATE-MATH-SARAH"
- Use descriptive codes that indicate it's a custom schedule

<strong>4. Add Subject and Teacher</strong>

For each subject-teacher pair in the course:

<strong>Select Subject:</strong>
- Click <strong>Subject</strong> dropdown
- Search or browse available subjects
- Format: [Title] - [Specification] ([Level])
- Example: "IGCSE - Math (Y. 10)"

<strong>Select Teacher:</strong>
- Click <strong>Teacher</strong> dropdown
- Search by teacher nickname
- Calendar appears automatically on the right
- Shows teacher's existing schedule and classes

<strong>5. Add Individual Time Slots</strong>

Click <strong>"Add Time Slot"</strong> for each class session:

<strong>Date</strong> (required):
- Click to open date picker
- Select specific date for this class
- Format displays as: dd/mm/yy
- Each slot can be a completely different date

<strong>Time</strong> (required):
- Enter class start time
- 24-hour format (HH:MM)
- Operating hours: 6:00 AM - 9:00 PM
- Example: 14:00 for 2:00 PM

<strong>Hours</strong> (required):
- Select: 0, 1, 2, or 3 hours
- Part of class duration

<strong>Minutes</strong> (required):
- Select: 0, 15, 30, or 45 minutes
- Combines with hours for total duration
- Example: 1 hour + 30 minutes = 1.5 hour class

<strong>Delete Time Slot</strong>:
- Click <strong>trash icon</strong> to remove slot
- Immediately updates total hours

<strong>6. Add More Time Slots</strong>

Keep clicking <strong>"Add Time Slot"</strong> to add more sessions:
- Each slot is independent
- Dates don't need to be consecutive
- Times can vary for each session
- Example schedule:
  - Jan 5, 14:00, 2 hours
  - Jan 8, 16:00, 1.5 hours
  - Jan 12, 10:00, 2 hours
  - Jan 15, 14:00, 1.5 hours

<strong>7. Add Multiple Subjects (Optional)</strong>

Click <strong>"Add Another Subject/Teacher"</strong> for multi-subject courses:
- Each subject gets its own schedule
- Different teachers for different subjects
- Each can have multiple time slots
- Delete entire subject blocks with trash icon
- Example: Math with Teacher A, English with Teacher B

<strong>8. Review Auto-Calculated Fields</strong>

System automatically calculates:

<strong>Start Date (AUTO)</strong>:
- Earliest date from all time slots
- Updates when you add/remove slots
- Shown in green with (AUTO) label

<strong>End Date (AUTO)</strong>:
- Latest date from all time slots
- Updates automatically
- Determines course duration

<strong>Total Hours (AUTO)</strong>:
- Sum of all class durations
- Formula: (hours + minutes/60) for each slot
- Example: 
  - Slot 1: 2 hours
  - Slot 2: 1.5 hours
  - Slot 3: 2 hours
  - Total: 5.5 hours

<strong>9. Add Course Information</strong>

<strong>Notes</strong> (optional):
- Book details
- Materials needed
- Special instructions
- Student-specific notes

<strong>Price</strong> (required):
- Total course fee
- Must be 0 or greater
- Enter in local currency

<strong>Register Capacity</strong> (required):
- Maximum students allowed
- Minimum: 1
- Set to 1 for truly private classes

<strong>10. Set Course Type</strong>

<strong>Private Class Checkbox</strong>:
- ☑ <strong>Checked</strong>: Private/individual instruction
- ☐ <strong>Unchecked</strong>: Group class
- Affects visibility and enrollment rules

<strong>11. Monitor Teacher Availability</strong>

The calendar shows:
- <strong>Blue events</strong>: Teacher's existing classes
- <strong>Green events</strong>: Teacher's blocked times/events
- Updates when selecting different teachers
- Check before adding each time slot
- Scroll to see different time periods

<strong>Calendar Features</strong>:
- Toggle: Month / Week / Day view
- Navigation: Previous / Next / Today buttons
- Time slots: 10:00 AM - 8:00 PM display
- Click events to see details

<strong>12. Submit Course</strong>

Click <strong>"Create Course"</strong>:

<strong>If Schedule Conflicts Detected</strong>:
- Warning popup appears
- Lists all conflicting dates/times
- Shows: Teacher, Date, Time range
- Two options:
  - <strong>"Yes, proceed"</strong>: Create anyway (override conflicts)
  - <strong>"No, I'll revise"</strong>: Go back and adjust

<strong>If No Conflicts</strong>:
- Confirmation modal appears
- Table shows all scheduled sessions
- Columns: Date, Time, Teacher, Subject
- Sessions sorted chronologically
- Review carefully before proceeding

<strong>Final Confirmation</strong>:
- Click <strong>"Confirm"</strong> to create course
- Click <strong>X</strong> to cancel and revise

<strong>Success</strong>:
- "Class created successfully" message
- Form clears automatically
- Course now in system database

<strong>Use Cases for Random Courses</strong>

<strong>Makeup Classes</strong>:
- Student missed regular class
- Need to schedule make-up session
- One-time replacement class

<strong>Intensive Programs</strong>:
- Short-term bootcamps
- Holiday programs
- Exam preparation sprints
- Irregular meeting patterns

<strong>Custom Schedules</strong>:
- Student requested specific dates
- Accommodating student availability
- Trial classes before regular enrollment
- Flexible tutoring arrangements

<strong>Event-Based Learning</strong>:
- Workshop series
- Guest lecturer sessions
- Special topic seminars
- Project-based learning with varied timings

<strong>Examples</strong>

<strong>Example 1: SAT Makeup Course</strong>
- Course Code: MAKEUP-SAT-MATH-001
- Subject: SAT - Math (G. 11)
- Teacher: John
- Time Slots:
  - Jan 10, 16:00, 2 hours
  - Jan 15, 14:00, 2 hours
  - Jan 20, 16:00, 2 hours
- Total: 6 hours over 10 days
- Private class: Yes

<strong>Example 2: Holiday Intensive</strong>
- Course Code: HOLIDAY-IELTS-DEC
- Subject: IELTS - Speaking (Advanced)
- Teacher: Emma
- Time Slots:
  - Dec 26, 10:00, 2 hours
  - Dec 27, 10:00, 2 hours
  - Dec 28, 10:00, 2 hours
  - Dec 29, 10:00, 2 hours
  - Dec 30, 10:00, 2 hours
- Total: 10 hours over 5 days
- Private class: No (capacity: 4)

<strong>Example 3: Multi-Subject Custom</strong>
- Course Code: CUSTOM-IGCSE-SARAH
- Subject 1: IGCSE - Physics (Y. 10), Teacher: Mike
  - Jan 5, 14:00, 1.5 hours
  - Jan 12, 14:00, 1.5 hours
  - Jan 19, 14:00, 1.5 hours
- Subject 2: IGCSE - Math (Y. 10), Teacher: Lisa
  - Jan 7, 16:00, 1.5 hours
  - Jan 14, 16:00, 1.5 hours
  - Jan 21, 16:00, 1.5 hours
- Total: 9 hours, 6 sessions

<strong>Best Practices</strong>

<strong>Planning:</strong>
- Coordinate with teacher before creating
- Confirm student availability for all dates
- Check for school holidays and breaks
- Plan logical progression of topics

<strong>Scheduling:</strong>
- Space sessions appropriately
- Allow time for homework/practice
- Consider student energy levels
- Avoid too many sessions in short time

<strong>Documentation:</strong>
- Use clear course codes
- Add detailed notes about arrangement
- Document reason for custom schedule
- Include any special agreements

<strong>Calendar Usage:</strong>
- Check calendar before each slot
- Look for patterns in conflicts
- Consider teacher commute time
- Verify against school schedule

<strong>Tips</strong>
- Random courses perfect for flexibility
- Check all dates in teacher calendar
- Space sessions for better retention
- Price accordingly for custom scheduling
- Communicate clearly with parents
- Document special arrangements
- Keep capacity realistic
- Review confirmation table carefully
- Save course code for reference
- Export schedule for teacher

<strong>Troubleshooting</strong>

<strong>Auto-dates not updating?</strong>
- Ensure all dates are entered
- Check date format is valid
- Try adding/removing a slot to trigger update

<strong>Can't add more slots?</strong>
- Check browser console for errors
- Try refreshing the page
- Ensure all current slots are complete

<strong>Teacher calendar not showing?</strong>
- Teacher must be selected first
- Check teacher has schedule configured
- Try selecting a different teacher

<strong>Overlap warnings on flexible schedule?</strong>
- Review if overlap is acceptable
- Students might be different
- Teacher might have approved
- Proceed if intentional

<strong>Total hours seems wrong?</strong>
- Verify each slot's hours and minutes
- Check no duplicate slots
- Ensure dates are unique
- Refresh and recalculate
`,
  image: "/manual-images/createcourse-random.png",
},
          {
  id: 7,
  title: "Course",
  description: `
<strong>Managing Existing Courses</strong>

The Course page provides a comprehensive view of all courses in the system with powerful filtering, editing, and management capabilities.

<strong>1. Access Course Management</strong>
- Navigate to <strong>Admin Menu → Course</strong>
- Main table displays all courses with detailed information
- Courses from all branches visible (based on your role)

<strong>2. Understanding the Course Table</strong>

<strong>Column Details:</strong>

<strong>Branch</strong>:
- Color-coded tags for easy identification
- Blue, Green, Magenta for different branches
- Superadmin sees all branches
- Admin sees only their branch

<strong>Course Code</strong>:
- Unique identifier (clickable link)
- Click to view detailed course information
- Opens course detail page

<strong>Schedule</strong>:
- <strong>Schedule button</strong> - View all class sessions
- Shows complete timeline of classes
- Displays teacher, date, time for each session

<strong>Start Date</strong>:
- Course begins date
- Sorted by default (newest first)
- Click column header to re-sort

<strong>End Date</strong>:
- Course completion date
- Calculated from last class session

<strong>Total Hours</strong>:
- Planned course duration
- Calculated from course setup
- Format: HH:MM hrs

<strong>Used Hours</strong>:
- Actual hours scheduled
- ⚠️ Warning icon if less than total hours
- Red text indicates hour mismatch
- Helps identify incomplete schedules

<strong>Note</strong>:
- Book details and materials
- Additional course information
- Special instructions

<strong>Private/Group</strong>:
- Blue tag: Private class
- Green tag: Group class

<strong>Platform</strong>:
- 🎥 Online: Video conferencing
- 🌐 Onsite: At branch location
- 🏠 At Home: Teacher visits student

<strong>Capacity</strong>:
- Maximum students allowed
- Set during course creation

<strong>Reg (Registrations)</strong>:
- Current number of enrolled students
- Real-time count
- Important for capacity management

<strong>Price</strong>:
- <strong>Base Price</strong>: Blue text, single rate
- <strong>Package Pricing</strong>: Green text, multiple options
  - Shows: Number of classes, Price, Validity period
  - Example: "10 classes: 15,000 THB (3 months)"
- Dashed line separates base and package prices
- Shows "-" if no pricing configured

<strong>Created By</strong>:
- Admin nickname who created course
- Red tag with name
- Creation date below

<strong>Actions</strong>:
- ✏️ <strong>Edit button</strong>: Modify course details
- 🗑️ <strong>Delete button</strong>: Remove course
  - Disabled if students registered
  - Requires confirmation

<strong>3. Editing Course Details</strong>

Click <strong>Edit button</strong> (pencil icon):

<strong>Editable Fields:</strong>

<strong>Course Code</strong>:
- Change course identifier
- Must be unique
- Required field

<strong>Note</strong>:
- Update materials information
- Add special instructions
- Text area for longer notes

<strong>Type (Private/Group)</strong>:
- Change class type
- Affects pricing and capacity rules

<strong>Platform</strong>:
- Switch between Onsite/Online/At Home
- Important for logistics

<strong>Register Capacity</strong>:
- Adjust maximum students
- Minimum: 1
- Cannot be less than current registrations

<strong>Price</strong>:
- Update course fee
- Format: ฿ XX,XXX.XX
- 🔒 <strong>Locked if students registered</strong>
- Tooltip explains restriction
- Prevents pricing changes mid-course

<strong>Save/Cancel</strong>:
- <strong>Save</strong>: Apply changes
- <strong>Cancel</strong>: Discard changes
- Confirmation messages appear

<strong>4. Deleting Courses</strong>

Click <strong>Delete button</strong> (trash icon):

<strong>Delete Restrictions</strong>:
- Button disabled if students registered
- Tooltip: "Cannot Delete With Registrations"
- Prevents accidental data loss
- Protects student records

<strong>Delete Confirmation</strong>:
- Warning dialog appears
- Message: "This action is irreversible"
- Two options:
  - <strong>Yes</strong>: Permanently delete
  - <strong>No</strong>: Cancel deletion
- Includes red warning icon

<strong>After Deletion</strong>:
- Course removed immediately
- Success message appears
- All related data deleted
- Cannot be undone

<strong>5. Understanding Hour Mismatch</strong>

<strong>What It Means</strong>:
- Total Hours: Planned/purchased hours
- Used Hours: Actually scheduled hours
- Mismatch: Used < Total

<strong>Why It Happens</strong>:
- Changes time in schedule by admin

<strong>How to Find</strong>:
- Red text in Used Hours column
- ⚠️ Warning icon
- Use Hour Status filter
- Review regularly

<strong>How to Fix</strong>:
- Schedule remaining left hours and update

<strong>6. Course Detail Page</strong>

Click <strong>Course Code</strong> link to open:
- Full course information
- List of enrolled students
- Complete schedule
- Attendance records


<strong>7. Troubleshooting</strong>

<strong>Course not appearing?</strong>
- Check branch filter setting
- Verify it matches search criteria
- Click Refresh button
- Check with course creator

<strong>Cannot edit price?</strong>
- Students already registered
- This is intentional protection
- Create new course if needed
- Contact superadmin for exceptions

<strong>Delete button disabled?</strong>
- Students registered in course
- Must unenroll students first
- Or course must complete
- Protection against data loss

<strong>Hour mismatch showing incorrectly?</strong>
- Check actual schedule
- Verify class durations
- Refresh the page
- Contact technical support if persists

<strong>Filters not working?</strong>
- Clear all filters and try again
- Refresh the page
- Check date format
- Verify data in filters exists
`,
  image: "/manual-images/admincourse.png",
},
         {
  id: 8,
  title: "Class Change",
  description: `
<strong>Viewing Class Schedule Changes</strong>

The Class Change page shows a complete log of all schedule modifications made to classes.

<strong>1. Access the Page</strong>
- Navigate to <strong>Admin Menu → Class Change</strong>
- View table of all schedule changes

<strong>2. Search and Filter</strong>

<strong>Branch Filter</strong> (Superadmin only):
- Dropdown at top left
- Select specific branch or view all branches

<strong>Search Bar</strong>:
- Search by: Class ID, Course Code, Subject, or Teacher
- Type to filter results in real-time
- Click clear (X) to reset search

<strong>Results Counter</strong>:
- Shows number of records found below search bar

<strong>Export CSV Button</strong>:
- Click to download all visible changes
- File name: class-changes-YYYY-MM-DD.csv
- Opens in Excel for analysis

<strong>3. Understanding the Table</strong>

<strong>Branch</strong>: Color-coded branch tag

<strong>Class Code</strong>: Course identifier

<strong>ID</strong>: Individual class session number

<strong>Subject</strong>: What subject was taught

<strong>Teacher</strong>: Teacher's nickname

<strong>Original Date/Time/Hour</strong>:
- When class was originally scheduled
- What the schedule was before change

<strong>New Date/Time/Hour</strong>:
- When class was moved to
- Updated schedule after change

<strong>Change Reason</strong>:
- Tag showing why changed
- Options: Student Request, Teacher Unavailable, Facility Issue, Holiday, Weather, Emergency, Administrative, Other

<strong>Note</strong>:
- Additional details or explanation
- Hover to see full text

<strong>Changed By</strong>:
- Admin nickname who made the change

<strong>Updated At</strong>:
- Date and time when change was made

<strong>4. Table Controls</strong>

<strong>Sorting</strong>:
- Click any column header to sort
- Click again to reverse order

<strong>Pagination</strong>:
- Change page size at bottom: 10, 20, 50, 100 records
- Jump to specific page
- Navigation arrows for previous/next

<strong>Scrolling</strong>:
- Scroll horizontally to see all columns
- Class Code column stays fixed when scrolling

<strong>5. Common Tasks</strong>

<strong>Find Teacher's Changes</strong>:
1. Type teacher nickname in search
2. Review their change history

<strong>Check Recent Changes</strong>:
1. Sort by "Updated At" column (newest first)
2. Review recent modifications

<strong>Export for Reports</strong>:
1. Apply any filters needed
2. Click Export CSV button
3. Open file in Excel

<strong>View Specific Course Changes</strong>:
1. Type course code in search
2. See all changes for that course

<strong>6. Tips</strong>
- Use search before scrolling through pages
- Check "Note" field for important details
- Export regularly for record keeping
- Sort by date to see patterns
- Compare original vs new times for accuracy
`,
  image: "/manual-images/classchange.png",
},
          {
  id: 9,
  title: "Confirm Hours",
  description: `
<strong>Confirming Teacher Class Hours</strong>

The Confirm Hours page allows admins to verify and approve teacher-reported class hours for payroll purposes.

<strong>1. Access the Page</strong>
- Navigate to <strong>Admin Menu → Confirm Hours</strong>
- View all classes requiring confirmation

<strong>2. Summary Statistics Cards</strong>

Four cards at the top show:
- <strong>Total Classes</strong>: All classes in the period (Blue)
- <strong>Teacher Confirmed</strong>: Classes teachers marked as completed (Green)
- <strong>Teacher Pending</strong>: Classes teachers haven't confirmed yet (Yellow)
- <strong>Admin Confirmed</strong>: Classes you've approved (Purple)

<strong>3. Filters</strong>

<strong>Branch</strong> (Superadmin only):
- Dropdown to filter by branch
- Select "All Branches" to see everything

<strong>Date Range</strong>:
- Click start date field to pick beginning date
- Click end date field to pick ending date
- Shows classes within selected period

<strong>Tutor</strong>:
- Dropdown to filter by teacher
- Select "All Tutors" to see everyone

<strong>Status</strong>:
- Teacher confirmation status
- <strong>Confirmed</strong>: Teacher marked as complete
- <strong>Pending</strong>: Teacher hasn't confirmed yet

<strong>Admin Status</strong>:
- Your approval status
- <strong>Admin Confirmed</strong>: You've approved
- <strong>Pending</strong>: Awaiting your approval

<strong>Export CSV Button</strong>:
- Downloads filtered results to Excel
- File name includes date range

<strong>4. Understanding the Table</strong>

<strong>Branch</strong>: Color-coded branch tag

<strong>No</strong>: Row number for reference

<strong>Date</strong>: When class was held

<strong>Class Code</strong>: Course identifier (blue tag)

<strong>Class ID</strong>: Unique class session number

<strong>Subject</strong>: What was taught (hover for full name)

<strong>Teacher</strong>: Teacher's nickname

<strong>Time</strong>: Class start and end time

<strong>Hour</strong>: Class duration (purple tag)

<strong>Tutor Confirm</strong>:
- ✅ Green tag: Teacher confirmed
- ⏰ Yellow tag: Teacher pending

<strong>Admin Approve</strong>:
- ✅ Green tag: "Admin Confirmed" (already approved by you)
- 🔵 Blue button: "Admin Confirm" (click to approve)
  - Clicking shows confirmation popup
  - Click "Yes" to confirm
  - Click "No" to cancel

<strong>Approved By</strong>: Admin nickname who confirmed

<strong>5. Confirming Hours</strong>

To confirm teacher hours:
1. Find classes with blue "Admin Confirm" button
2. Verify the class details are correct
3. Click "Admin Confirm" button
4. Popup asks: "Are you sure you want to confirm this class?"
5. Click "Yes" to approve
6. Status changes to "Admin Confirmed" (green tag)
7. Your nickname appears in "Approved By" column

<strong>6. Table Controls</strong>

<strong>Sorting</strong>:
- Click "Date" column to sort by date

<strong>Column Filters</strong>:
- Click filter icon on Teacher, Status, or Admin Status columns
- Select options to filter
- Click "OK"

<strong>Pagination</strong>:
- Change rows per page at bottom
- Navigate between pages

<strong>Footer Summary</strong>:
- <strong>Total Hours</strong>: Sum of all displayed class hours (blue)
- <strong>Total Classes</strong>: Number of classes shown
- <strong>Confirmed</strong>: Count of teacher-confirmed classes (green)
- <strong>Pending</strong>: Count of pending classes (yellow)
- <strong>Admin Confirmed</strong>: Count you've approved (purple)

<strong>7. Common Tasks</strong>

<strong>Confirm All Classes for a Teacher</strong>:
1. Select teacher from Tutor filter
2. Filter by Status: "Confirmed" (teacher already confirmed)
3. Filter by Admin Status: "Pending" (not yet approved by you)
4. Review each class
5. Click "Admin Confirm" for each one

<strong>Review Pending Teacher Confirmations</strong>:
1. Filter by Status: "Pending"
2. See which teachers haven't confirmed their classes
3. Follow up with those teachers

<strong>Export Monthly Hours</strong>:
1. Set date range for the month
2. Select teacher from Tutor filter
3. Click "Export CSV"
4. Use for payroll processing

<strong>Verify Last Week's Classes</strong>:
1. Set date range for last week
2. Review all classes
3. Confirm hours for payment

<strong>8. Tips</strong>
- Confirm hours regularly (weekly recommended)
- Check date range matches pay period
- Verify teacher confirmed before you approve
- Export before processing payroll
- Use filters to focus on pending items
- Check footer totals for accuracy
- Sort by date to review chronologically
`,
  image: "/manual-images/confirmhours.png",
},
          {
  id: 10,
  title: "Announce",
  description: `
<strong>Creating and Managing Announcements</strong>

The Announce page allows admins to create, edit, and manage announcements visible to students and teachers.

<strong>1. Access the Page</strong>
- Navigate to <strong>Admin Menu → Announce</strong>
- Split screen: Creation form (left) and announcements list (right)

<strong>2. Creating an Announcement</strong>

Fill in the form on the left side:

<strong>Title</strong> (required):
- Enter announcement headline
- Keep it clear and concise

<strong>Message</strong> (required):
- Enter full announcement text
- Text area for longer messages
- Be clear and detailed

<strong>Link</strong> (optional):
- Add URL for more information
- Example: Registration link, document link
- Shows as clickable icon in list

<strong>Image URL</strong> (optional):
- Add image link
- Shows as image icon in list
- Viewers can click to see image

<strong>Posted By</strong> (required):
- Author name (default: "Admin")
- Change if needed

<strong>Date</strong> (required):
- Click to select date
- Defaults to today
- Shows when announcement was made

<strong>Buttons</strong>:
- <strong>Reset</strong>: Clear form
- <strong>Create Announcement</strong>: Publish announcement

<strong>3. Viewing Announcements</strong>

The table on the right shows all announcements:

<strong>Title</strong>: Announcement headline (hover to see full text)

<strong>Message</strong>: Preview of message (hover to see full text)

<strong>Link</strong>: 🔗 Click icon to open link (if provided)

<strong>Image</strong>: 🖼️ Click icon to view image (if provided)

<strong>By</strong>: Who posted it

<strong>Date</strong>: When it was posted

<strong>Actions</strong>:
- ✏️ Blue Edit button: Modify announcement
- 🗑️ Red Delete button: Remove announcement

<strong>4. Editing an Announcement</strong>

1. Click blue Edit button on the announcement
2. Form fills with existing data
3. Make your changes
4. Button changes to "Update Announcement"
5. Click to save changes
6. Click Reset to cancel editing

<strong>5. Deleting an Announcement</strong>

1. Click red Delete button
2. Confirmation popup appears
3. Shows announcement title
4. Click "Yes, Delete" to confirm
5. Click "Cancel" to keep it
6. Success message appears after deletion

<strong>6. Tips</strong>
- Write clear, actionable titles
- Keep messages concise but informative
- Add links for more details or registration
- Use images to increase engagement
- Review before publishing
- Edit anytime if information changes
- Delete outdated announcements regularly
- Most recent announcements show first
`,
  image: "/manual-images/announce.png",
},
          {
  id: 11,
  title: "Teacher-Subject",
  description: `
<strong>Managing Teacher-Subject Matching</strong>

The Teacher-Subject page allows you to link teachers with subjects they can teach, organized by language.

<strong>1. Access the Page</strong>
- Navigate to <strong>Admin Menu → Teacher-Subject</strong>
- Three main tabs available

<strong>2. Tab 1: Create & Find Matches</strong>

<strong>Left Card - Create Match:</strong>

Fill in three dropdowns:
- <strong>Select Teacher</strong>: Choose from list (searchable)
- <strong>Select Subject</strong>: Choose subject to match (searchable)
- <strong>Select Language</strong>: Thai or English

Click <strong>Create Match</strong> button to link teacher with subject

<strong>Right Card - Find Matched Teachers:</strong>

Filter to find teachers:
- <strong>Select Subject</strong>: Pick a subject
- <strong>Select Language</strong>: Thai or English
- Results show all teachers who can teach that subject in selected language

<strong>3. Tab 2: Subject Overview</strong>

View organized by language (Thai/English tabs):

<strong>Display:</strong>
- Grid of cards, one per subject
- Each card shows subject name
- Lists all teachers assigned to that subject
- Empty if no teachers matched

<strong>Use:</strong>
- Quick overview of teacher coverage
- Identify subjects needing more teachers
- See teacher distribution

<strong>4. Tab 3: Manage Matches</strong>

Table showing all matches:

<strong>Columns:</strong>
- <strong>Teacher</strong>: Teacher nickname and name
- <strong>Subject</strong>: Subject title
- <strong>Language</strong>: Thai (blue tag) or English (green tag)
- <strong>Actions</strong>: 🗑️ Delete button

<strong>Features:</strong>
- Filter by language (click filter icon)
- Sort by clicking column headers
- Pagination at bottom
- Shows total matches count

<strong>Deleting a Match:</strong>
1. Click delete button (trash icon)
2. Confirmation popup appears
3. Click "Yes" to confirm
4. Click "No" to cancel

<strong>5. Common Tasks</strong>

<strong>Add Teacher to Subject:</strong>
1. Go to Tab 1 (Create & Find Matches)
2. Select teacher from dropdown
3. Select subject
4. Select language
5. Click Create Match

<strong>Find Available Teachers:</strong>
1. Go to Tab 1
2. Use right card (Find Matched Teachers)
3. Select subject and language
4. View results list

<strong>View Subject Coverage:</strong>
1. Go to Tab 2 (Subject Overview)
2. Switch between Thai/English tabs
3. Browse cards to see assignments

<strong>Remove Teacher Assignment:</strong>
1. Go to Tab 3 (Manage Matches)
2. Find the match in table
3. Click delete button
4. Confirm deletion

<strong>6. Tips</strong>
- Use searchable dropdowns to find quickly
- Match teachers to subjects they specialize in
- Assign both languages if teacher is bilingual
- Check Subject Overview regularly for gaps
- Remove outdated matches when teachers leave
- Filter by language in Manage Matches table
- Total matches shown at table bottom
`,
},
        ],
      },
      {
        section: "Teacher Schedule",
        topics: [
         {
  id: 12,
  title: "Teacher Schedule",
  description: `
<strong>Viewing Teacher Schedules</strong>

View and compare schedules for up to 5 teachers simultaneously on a shared calendar.

<strong>1. Access the Page</strong>
- Navigate to Sidebar menu<strong>Teacher Schedule</strong>

<strong>2. Filters</strong>

<strong>Search Box</strong>:
- Search by course code or subject name
- Filters calendar events in real-time

<strong>Select Teachers</strong>:
- Multi-select dropdown (max 5 teachers)
- Each teacher has a color code
- Shows color tag next to name
- Must select at least 1 teacher to view calendar

<strong>Select Branches</strong>:
- Filter by branch location
- Multi-select available
- Shows only classes from selected branches

<strong>3. Calendar Display</strong>

<strong>Views</strong>:
- Switch between Month, Week, or Day view
- Use navigation arrows to change dates
- Click "Today" to return to current date

<strong>Event Information</strong>:
Each class shows:
- Time range (HH:MM - HH:MM)
- ✅ or ❌ Student confirmation status
- Course code
- Subject name with class ID
- Teacher nickname
- Platform icon: 💻 Online, 📍 Onsite, or 🏠 At Home
- Branch name with 🏢 icon
- Color-coded by teacher

<strong>4. Viewing Details</strong>

Click any class event to see:
- Full class information
- Students enrolled
- Class history and changes

<strong>5. Tips</strong>
- Select multiple teachers to compare schedules
- Use color coding to identify teachers quickly
- Switch to Week view for detailed planning
- Use Day view to see hourly breakdown
- Filter by branch for location-specific scheduling
`,
  image: "/manual-images/teacherschedule.png",
},
        ],
      },
      {
        section: "Branch Schedule",
        topics: [
          {
  id: 13,
  title: "Branch Schedule - Confirm Class",
  description: `
<strong>Managing Branch Schedule and Confirming Classes</strong>

View and modify all classes across the branch with drag-and-drop editing, ensuring schedule changes don't exceed course hours.

<strong>1. Access the Page</strong>
- Navigate to Sidebar menu<strong>Branch Schedule</strong>

<strong>2. Filters</strong>

<strong>Branch Filter</strong> (Superadmin only):
- Select specific branch or view all branches

<strong>Search Box</strong>:
- Search by course code or subject name
- Real-time filtering

<strong>Select Teachers</strong>:
- Multi-select dropdown to filter calendar
- View schedule for specific teachers
- Shows color-coded tags

<strong>Refresh Button</strong>:
- Reloads all calendar data
- Updates class and teacher schedules

<strong>3. Calendar Views</strong>

Switch between:
- <strong>Week View</strong>: See full week schedule
- <strong>Day View</strong>: Detailed daily schedule

<strong>Navigation</strong>:
- Previous/Next arrows
- "Today" button to return to current date

<strong>4. Class Event Display</strong>

Each class shows:
- Time range with confirmation icon (✅ or ❌)
- Course code
- Subject name with class ID
- Teacher nickname
- Platform: 💻 Online, 📍 Onsite, 🏠 At Home
- Branch name with 🏢 icon
- Color-coded by teacher

<strong>5. Moving Classes (Drag & Drop)</strong>

<strong>To Reschedule a Class:</strong>
1. Click and drag the class event
2. Drop at new date/time slot
3. System checks:
   - <strong>Approved Status</strong>: Cannot move approved classes
   - <strong>Teacher Conflict</strong>: Checks for overlapping classes/events
   - <strong>Time Validation</strong>: Ensures valid schedule

<strong>If Conflicts Detected:</strong>
- Popup appears: "Time Slot Conflict"
- Message explains the overlap
- Change automatically reverts
- Choose different time slot

<strong>If No Conflicts:</strong>
- Change Reason popup appears
- Must select reason and add note
- Click "Submit" to confirm change
- Change is logged in system

<img src="/manual-images/calendar3.png" alt="Change Reason Modal" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />

<strong>6. Resizing Classes (Change Duration)</strong>

<strong>To Change Class Duration:</strong>
1. Click and drag bottom edge of event
2. Expand or shrink duration
3. System validates:
   - <strong>Approved Status</strong>: Cannot resize approved classes
   - <strong>Teacher Conflict</strong>: Checks for overlaps
   - <strong>Total Hours Limit</strong>: Critical validation

<strong>Total Hours Validation:</strong>
- Each course has maximum allowed hours
- System calculates: Current used hours - old duration + new duration
- If exceeds limit:
  - Popup shows: "Exceeds Total Hours"
  - Displays allowed hours and excess amount
  - Change automatically reverts
  - Must stay within course hours

<strong>If Within Limits:</strong>
- Change Reason popup appears
- Enter reason and note
- Submit to confirm

<strong>7. Change Reason Popup</strong>

When moving or resizing, you must provide <strong>Change Reason</strong>

<strong>Note</strong> (text field):
- Additional explanation
- Important details
- Follow-up actions needed

<strong>Buttons</strong>:
- <strong>Submit</strong>: Save the change
- <strong>Cancel</strong>: Revert to original

<img src="/manual-images/calendar2.png" alt="Confirm Class Details" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />

<strong>8. Viewing Class Details</strong>

Click any class event to see:
- Full class information
- Enrolled students
- Class history
- Previous changes made

<strong>9. Understanding Restrictions</strong>

<strong>Cannot Edit If:</strong>
- Class status is "Approved"
- Teacher has conflicting schedule
- Would exceed course total hours
- Overlaps with teacher's blocked time

<strong>Warning Messages:</strong>
- <strong>"Action Not Allowed"</strong>: Class is approved
- <strong>"Time Slot Conflict"</strong>: Overlap detected
- <strong>"Exceeds Total Hours"</strong>: Over course limit

<strong>10. Tips</strong>
- Check teacher availability before moving classes
- Always provide clear change reasons
- Verify course has enough remaining hours before resizing
- Use Week view for better overview
- Refresh after making multiple changes
- Review class details before editing
- Coordinate with teachers for major changes
- Document all changes in notes field
`,
  image: "/manual-images/calendar1.png",
},
        ],
      },
      {
        section: "Class List",
        topics: [
          {
  id: 14,
  title: "Class List - Change Date/Time/Subject/Tutor/Room",
  description: `
<strong>Managing Individual Classes</strong>

View all classes in a table format with inline editing capabilities for date, time, subject, teacher, and room assignments.

<strong>1. Access the Page</strong>
- Navigate to Sidebar menu<strong> Class List</strong>

<strong>2. Filters and Search</strong>

<strong>Branch Filter</strong> (Superadmin only):
- Dropdown to select specific branch
- Shows "All Branches" by default

<strong>Date Range</strong>:
- <strong>From</strong>: Click to select start date
- <strong>To</strong>: Click to select end date
- Shows classes within selected range

<strong>Search Box</strong>:
- Search by: Course code, Subject name, or Class ID
- Real-time filtering

<strong>Teacher Filter</strong>:
- Dropdown to filter by specific teacher
- Select "All Teachers" to see everyone

<strong>Export CSV Button</strong>:
- Downloads all filtered classes to Excel
- File name includes current date

<strong>3. Understanding the Table</strong>

<strong>Confirm</strong>: ✅ (confirmed) or ❌ (not confirmed) - Student confirmation status

<strong>Branch</strong>: Color-coded branch tag

<strong>ID</strong>: Class session ID number

<strong>Date</strong>: Class date with 📅 edit icon

<strong>Day</strong>: Day of week (Monday, Tuesday, etc.)

<strong>Time</strong>: Start - End time with ✏️ edit icon

<strong>Hours</strong>: Class duration

<strong>Course</strong>: Course code

<strong>Type</strong>: Private or Group (color-coded badge)

<strong>Platform</strong>: 💻 Online, 📍 Onsite, or 🏠 At Home

<strong>Subject</strong>: Subject name with 📖 edit icon

<strong>Tutor</strong>: Teacher nickname with 👨‍🏫 edit icon

<strong>Room</strong>: Room number with branch initial and ✏️ edit icon

<strong>Teacher Approval</strong>: Approved, Pending, or Cancelled status

<strong>4. Changing Class Date</strong>

<strong>To Edit Date:</strong>
1. Click 📅 calendar icon next to date
2. Date picker appears
3. Select new date
4. Click <strong>Save</strong> button

<strong>Change Reason Modal:</strong>
- <strong>Change Reason</strong> dropdown appears
- Select reason (Student Request, Teacher Unavailable, etc.)
- <strong>Note</strong> field: Add explanation
- Click <strong>Submit</strong> to confirm
- Change is logged in system

<strong>Restrictions:</strong>
- Cannot edit if status is "Approved"
- Edit icon won't appear for approved classes

<strong>5. Changing Class Time</strong>

<strong>To Edit Time:</strong>
1. Click ✏️ edit icon next to time
2. Dropdown appears with time options (08:00-23:00, every 15 min)
3. Select new start time
4. Click <strong>Save</strong>

<strong>Confirmation:</strong>
- Popup asks: "Confirm Time Update"
- Click <strong>Yes</strong> to proceed
- Click <strong>No</strong> to cancel
- End time auto-adjusts based on duration

<strong>6. Changing Subject</strong>

<strong>To Edit Subject:</strong>
1. Click 📖 book icon next to subject name
2. Searchable dropdown appears
3. Type to search or scroll through subjects
4. Select new subject
5. Click <strong>Save</strong>

<strong>Confirmation:</strong>
- Popup asks: "Confirm Subject Update"
- Click <strong>Yes</strong> to change
- Updates immediately after confirmation

<strong>7. Changing Teacher (Tutor)</strong>

<strong>To Edit Teacher:</strong>
1. Click 👨‍🏫 teacher icon next to tutor name
2. Searchable dropdown appears
3. Type teacher nickname or name
4. Select new teacher
5. Click <strong>Save</strong>

<strong>Confirmation:</strong>
- Popup asks: "Confirm Teacher Update"
- Click <strong>Yes</strong> to change
- Check teacher availability before assigning

<strong>8. Assigning Room</strong>

<strong>To Edit Room:</strong>
1. Click ✏️ edit icon next to room number
2. Dropdown shows available rooms
3. Format: (Branch Initial) Room Number
4. Select new room
5. Click <strong>Save</strong>

<strong>Confirmation:</strong>
- Popup asks: "Confirm Classroom Update"
- Click <strong>Yes</strong> to assign
- Only available rooms shown in list

<strong>9. Understanding Status Colors</strong>

<strong>Type Badges:</strong>
- <strong>Private</strong>: Blue badge
- <strong>Group</strong>: Green badge

<strong>Teacher Approval Status:</strong>
- <strong>Approved</strong>: Green badge - Cannot edit
- <strong>Pending</strong>: Orange badge - Can edit

<strong>10. Editing Restrictions</strong>

<strong>Cannot Edit When:</strong>
- Class status is "Approved"
- All edit icons disappear for approved classes
- This protects confirmed schedules

<strong>Can Edit When:</strong>
- Status is "Pending" or "Cancelled"
- Edit icons visible next to fields
- Changes require confirmation

<strong>11. Tips</strong>
- Use date range to focus on specific period
- Filter by teacher to manage their schedule
- Always check teacher availability before changing
- Verify room availability before assigning
- Export CSV before making bulk changes
- Cancel button available for all edit actions
- Changes require confirmation for safety
- Approved classes are protected from accidental edits
- Date changes require reason and note
- Table shows up to 100 rows per page (adjustable)
`,
  image: "/manual-images/classlist.png",
},
         
        ],
      },
      {
  section: "Quotation",
  topics: [
    {
      id: 19,
      title: "Create Bank Account",
      description: `
<strong>Managing Bank Accounts for Quotations</strong>

Add and manage bank account details that will appear on quotations for payment.

<strong>1. Access Bank Details</strong>
- Click <strong>"Create New Quotation"</strong> button
- In the drawer, click <strong>"Bank Details"</strong> tab
- Click <strong>"Add New Bank Details"</strong> button

<strong>2. Fill Bank Information</strong>

<strong>Branch</strong> (required):
- Admin: Shows your branch (cannot change)
- Superadmin: Select from dropdown

<strong>Bank Name</strong> (required):
- Enter bank name (e.g., "Bangkok Bank", "Kasikorn Bank")

<strong>Bank Branch</strong> (optional):
- Enter branch name or location

<strong>Account Name</strong> (required):
- Account holder name

<strong>Account Number</strong> (required):
- Full account number

<strong>3. Save Bank Details</strong>
- Click <strong>"Create Bank Details"</strong>
- Account added to list
- Available for use in quotations

<strong>4. Managing Bank Accounts</strong>

<strong>View List</strong>:
- Shows: Bank name, Branch, Account name, Last 4 digits
- Example: Bangkok Bank - John Doe - <strong><strong> 1234

<strong>Edit</strong>:
- Click ✏️ edit icon
- Modify details
- Click "Update"

<strong>Delete</strong>:
- Click 🗑️ delete icon
- Confirm deletion
- Cannot delete if used in quotations

<strong>5. Using Bank Details in Quotations</strong>

When creating quotation:
- <strong>Primary Bank</strong>: Required - main payment account
- <strong>Secondary Bank</strong>: Optional - alternative account
- Both show on printed quotation

<strong>6. Tips</strong>
- Add all company bank accounts
- Keep account details accurate
- Update if account changes
- Verify account numbers carefully
- Secondary account useful for different currencies
`,
      image: "/manual-images/create-bank.png",
    },
    {
      id: 20,
      title: "Create Temp Student",
      description: `
<strong>Managing Temporary Students</strong>

Create temporary student profiles for quotations before they become registered students.

<strong>1. Access Temp Students</strong>
- Click <strong>"Create New Quotation"</strong> button
- In the drawer, click <strong>"Temp Students"</strong> tab
- Click <strong>"Add New Temp Student"</strong> button

<strong>2. Fill Student Information</strong>

<strong>First Name</strong> (required):
- Student's first name

<strong>Last Name</strong> (required):
- Student's last name

<strong>Nickname</strong> (optional):
- Preferred name

<strong>Phone</strong> (optional):
- Contact number

<strong>Address</strong> (optional):
- Full address

<strong>3. Save Temp Student</strong>
- Click <strong>"Create Temp Student"</strong>
- Profile added to list
- Available for quotations

<strong>4. Managing Temp Students</strong>

<strong>View List</strong>:
- Shows: Name, Nickname, Phone, Address

<strong>Edit</strong>:
- Click ✏️ edit icon
- Modify information
- Click "Update"

<strong>Delete</strong>:
- Click 🗑️ delete icon
- Confirm deletion

<strong>5. Using Temp Students in Quotations</strong>

When creating quotation:
- Select <strong>"Temporary Student"</strong> type
- Choose from temp student dropdown
- Details auto-fill in form
- Or click "Manage temp students" to add new one

<strong>6. When to Use Temp Students</strong>

Use for:
- Prospective students requesting quotations
- Walk-in inquiries
- Students not yet registered
- Quick quotation generation

Later:
- Register as real student after enrollment
- Temp profile can remain for records

<strong>7. Tips</strong>
- Add minimal required info for quick quotations
- Update with more details as needed
- Use for trial period students
- Keep phone numbers accurate for follow-up
- Clean up old temp students periodically
`,
      image: "/manual-images/create-tempstudent.png",
    },
    {
      id: 21,
      title: "Create Quotation",
      description: `
<strong>Creating Student Quotations</strong>

Generate professional quotations for course packages and pricing.

<strong>1. Start New Quotation</strong>
- Click <strong>"Create New Quotation"</strong> button
- Quotation drawer opens

<strong>2. Select Branch</strong>
- Admin: Auto-set to your branch
- Superadmin: Choose from dropdown

<strong>3. Choose Student Type</strong>

<strong>Existing Student</strong>:
- Select from dropdown
- Search by name
- Details auto-fill

<strong>Temporary Student</strong>:
- Select from temp student list
- Or create new temp student
- Details auto-fill

<strong>4. Select Bank Accounts</strong>

<strong>Primary Bank</strong> (required):
- Main payment account
- Appears on quotation

<strong>Secondary Bank</strong> (optional):
- Alternative payment option
- Both banks shown if selected
<img src="/manual-images/create-quotation2.png" alt="Create Quotation" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>5. Add Quotation Items</strong>

<strong>Option 1 - From Existing Course</strong>:
- Click dropdown "Add from existing class"
- Select course
- Price auto-fills
- Shows: Course code, Title, Price

<strong>Option 2 - Custom Item</strong>:
- Click <strong>"Add Custom Item"</strong> (max 5 items)
- Fill fields:
  - <strong>Description</strong>: Enter item name
  - <strong>Price/Unit</strong>: Enter price
  - <strong>Quantity</strong>: Enter amount
  - <strong>Total</strong>: Auto-calculates

<strong>Edit Items</strong>:
- Change price, quantity anytime
- Total updates automatically
- Click 🗑️ to remove item

<strong>6. Apply Discounts and VAT</strong>

<strong>Discount</strong>:
- Enter discount amount in ฿
- Subtracts from subtotal

<strong>VAT Checkbox</strong>:
- Check to include 7% VAT
- Uncheck to exclude VAT
- Calculates on (Subtotal - Discount)

<strong>7. Add Remarks</strong>
- Text area for additional notes
- Shows on printed quotation
- Optional field

<strong>8. Review Totals</strong>

Display shows:
- <strong>Subtotal</strong>: Sum of all items
- <strong>Discount</strong>: Applied discount
- <strong>VAT (7%)</strong>: If enabled
- <strong>Total</strong>: Final amount in large text

<strong>9. Save Quotation</strong>
- Click <strong>"Create Quotation"</strong>
- Quotation number auto-generates
- Format: QO-B[BranchID]-[DateTime]
- Success message appears

<strong>10. View and Manage Quotations</strong>

<strong>Quotations List</strong>:
- Shows all quotations
- Columns: Branch, Number, Date, Student, Amount, Created By, Unique ID

<strong>Filter Options</strong>:
- Branch filter (Superadmin only)
- Date range picker
- Search by student name or quotation number
- Click "Filter" to apply

<strong>Actions</strong>:
- ✏️ <strong>Edit</strong>: Modify quotation
- 👁️ <strong>Preview</strong>: View PDF preview
- 🗑️ <strong>Delete</strong>: Remove quotation

<strong>11. Preview Quotation</strong>

Click preview (👁️) to see:
- Professional formatted document
- All items with prices
- Bank account details
- Student information
- Total breakdown

<strong>Preview Options</strong>:
- <strong>Valid for</strong>: Select expiration period (1 day - 3 months)
- <strong>Change Date</strong>: Override creation date if needed
- PDF ready for printing/sending

<strong>12. Export to CSV</strong>
- Click <strong>"Export CSV"</strong> button
- Downloads all filtered quotations
- File name includes date

<strong>13. Tips</strong>
- Create quotations for trial students first
- Include clear item descriptions
- Set appropriate expiration periods
- Use discounts for promotions
- Add remarks for special conditions
- Preview before sending to parents
- Keep quotation numbers for tracking
- Edit anytime before converting to registration
- Use existing courses to save time
- Maximum 5 custom items per quotation
`,
      image: "/manual-images/create-quotation1.png",
    },
  ],
},
      {
  section: "Register",
  topics: [
    {
      id: 22,
      title: "Register",
      description: `
<strong>Student Course Registration</strong>

Register students for courses and manage enrollment with pricing and payment details.

<strong>1. Access Registration</strong>
- Navigate to Sidebar menu <strong>Register</strong>

<strong>2. Select Branch</strong> (Superadmin only)
- Choose branch from dropdown
- Required before proceeding
- Shows available students and classes for that branch

<strong>3. Select Student</strong>

<strong>Search Student</strong>:
- Click dropdown under "Select Student"
- Type student name to search
- Select from filtered results

<strong>Student Details Display</strong>:
- Name with 👤 icon
- Address
- Phone number  
- Email address

<strong>4. Add Classes to Registration</strong>

<strong>Select Class</strong>:
- Click "Add Classes" dropdown
- Shows: Class code + Start date
- Classes already registered show "(Already Registered)" and are disabled
- Previously selected classes are also disabled

<strong>Class Information Shown</strong>:
- Class code
- Start date
- Price

<strong>Add Multiple Classes</strong>:
- Select first class, then select another
- No limit on number of classes
- Each appears as separate card

<strong>5. Review Selected Classes</strong>

<strong>Class Cards Display</strong>:
- Shows count: "Selected Classes (X)"
- Each class in its own card showing:
  - Class code as title
  - Start date
  - Price
  - 🗑️ Remove button (top right)
  - ℹ️ View Schedule button

<strong>View Schedule</strong>:
- Click "View Schedule" button
- Modal opens showing all class dates
- Shows: Date, Day, Time, Duration, Subject
- Helps student plan attendance

<strong>Remove Class</strong>:
- Click 🗑️ trash icon
- Class removed from selection
- Price recalculates automatically

<strong>6. Apply Pricing</strong>

<strong>Total Class Price</strong>:
- Auto-calculates sum of all selected classes
- Updates when adding/removing classes

<strong>Promotion/Discount</strong>:
- Enter discount amount
- Subtracts from total
- Format: 0.00

<strong>Price Before VAT</strong>:
- Shows: Total - Discount
- Displays automatically

<strong>VAT Checkbox</strong>:
- Check to add 7% VAT
- Shows VAT amount in blue tag when checked
- Calculates on (Total - Discount)

<strong>Total Price</strong>:
- Final amount in large green text
- Includes all calculations
- Format: 0.00 with 2 decimals

<strong>7. Confirm Registration</strong>

Click <strong>"Confirm Registration"</strong> button:

<strong>Requirements</strong>:
- Student must be selected
- At least 1 class must be added
- Total price must be greater than 0
- Button disabled if requirements not met

<strong>Confirmation Modal</strong>:
- Shows summary of registration
- Student name
- All selected classes (shows in cards)
- Each class shows: Code, Subject, Price
- Total class price
- Discount (if applied)
- VAT (if applied)
- Final total price in green

<strong>Review and Confirm</strong>:
- Click <strong>OK</strong> to complete registration
- Click <strong>Cancel</strong> to go back and edit

<strong>8. Success</strong>

After confirmation:
- Success modal appears with ✓ icon
- Message: "Successful Registration"
- Form automatically clears
- Ready for next registration

<strong>9. Understanding Registration Status</strong>

<strong>Already Registered Classes</strong>:
- System tracks existing registrations
- Prevents duplicate enrollment
- Shows in dropdown as disabled
- Message: "(Already Registered)"

<strong>System Validation</strong>:
- Checks for duplicates automatically
- Shows error if trying to add registered class
- Prevents conflicts

<strong>10. Common Tasks</strong>

<strong>Register Student for Single Class</strong>:
1. Select student
2. Choose class
3. Apply discount if needed
4. Check VAT if applicable
5. Confirm registration

<strong>Register for Multiple Classes</strong>:
1. Select student
2. Add first class
3. Add second class
4. Add more as needed
5. Review all classes in cards
6. Apply bulk discount
7. Confirm all together

<strong>View Class Schedule Before Registering</strong>:
1. Select student
2. Add class to selection
3. Click "View Schedule" button
4. Review dates and times
5. Decide if suitable
6. Remove if not suitable, or keep and continue

<strong>Apply Promotional Discount</strong>:
1. Select student and classes
2. Enter discount in "Promotion/Discount" field
3. See price update automatically
4. Confirm with reduced price

<strong>11. Tips</strong>
- Search students by typing in dropdown
- Review schedule before registering
- Apply discounts before confirming
- VAT adds 7% to final price
- Multiple classes can share one discount
- Check total before confirming
- Remove classes easily with trash icon
- Success modal confirms completion
- Form clears ready for next student
- Already registered classes are blocked automatically
- System prevents duplicate registrations
- View all class details before committing
- No need to calculate totals manually
`,
      image: "/manual-images/admin-register.png",
    },
  ],
},
      {
        section: "Sales",
        topics: [
          {
  id: 23,
  title: "Add Inventory",
  description: `The Inventory Management system allows you to add and track items available for sale to students.

<strong>Adding Inventory Items:</strong>
1. Enter a unique Product ID (required)
2. Enter the Item Name (required)
3. Add an optional Item Description for additional details
4. Select a Category from the dropdown:
   • Test
   • IELTS Book, TOEFL Book, GED Book, IGCSE Book
   • Other Book
   • Equipment
   • Others
5. Enter the Price per Unit in Baht (฿)
6. Click "Add Item" to save

<strong>Form Actions:</strong>
- Click "Add Item" to save the inventory entry
- Click "Reset" to clear all fields and start over

<strong>Important Notes:</strong>
- Product ID must be unique for each item
- Price must be 0 or greater
- All required fields must be filled before submitting
- Success or error messages will appear after submission

Once added, inventory items become available for sales transactions and can be tracked through the system.`,
  image: "/manual-images/addinventory.png",
},
          {
  id: 24,
  title: "Make Sales",
  description: `The POS (Point of Sale) system allows you to process sales transactions for students.

<strong>Making a Sale:</strong>
1. Select a customer from the dropdown at the top of the cart
2. Browse items by category using the left sidebar menu
3. Click on items to add them to the cart
4. Adjust quantities in the cart using the number input
5. Apply discount if needed (enter amount in THB)
6. Check "VAT (7%)" box if applicable
7. Select a Payment Method:
   • Cash
   • Bank Transfer (requires bank selection)
   • Credit Card
   • Online Payment
8. If Bank Transfer selected, choose the bank account
9. Click "Process Payment" to complete the sale

<strong>Managing the Cart:</strong>
- Click items to add them to cart
- Use number input to change quantity
- Click trash icon to remove items
- View subtotal, discount, VAT, and total in real-time

<strong>Additional Features:</strong>
- Click "Refresh & Clear" to reset all data and reload items
- Filter items by category (All, Test, Books, Equipment, etc.)
- Cart shows item name, quantity, price, and subtotal
- Payment button is disabled until all required fields are filled

<strong>Payment Requirements:</strong>
- Customer must be selected
- Cart must have at least one item
- Payment method must be chosen
- For Bank Transfer, a bank account must be selected

The system automatically validates all requirements before processing the payment.`,
  image: "/manual-images/pos.png",
},
          {
  id: 25,
  title: "Update Product Delivery",
  description: `The Delivery Report tracks all product deliveries from sales transactions and allows you to manage delivery status.

<strong>Viewing Delivery Orders:</strong>
- All sales transactions appear in the delivery report
- Table shows: Order Date, Student Name, Item Details, Quantity, Status, Delivery Date
- Use filters to find specific orders:
  - Date Range: Select start and end dates
  - Status: Filter by Pending/Delivered/Cancelled
  - Search: Search by student name, item name, or document number
- Click "Apply" to filter results, "Reset" to clear filters

<strong>Delivery Status Types:</strong>
- <strong>Pending</strong> (Orange) - Awaiting delivery
- <strong>Delivered</strong> (Green) - Successfully delivered
- <strong>Cancelled</strong> (Red) - Order cancelled

<strong>Managing Deliveries:</strong>

<strong>For Pending Orders:</strong>
1. Click "Deliver" button to mark as delivered
   - Confirms delivery and records delivery date
   - Records your admin name as deliverer
2. Click "Cancel" to mark as cancelled
   - Use for cancelled or refunded orders

<strong>For Delivered Orders:</strong>
1. Click "Print DO" to generate delivery order document
   - Opens printable delivery slip with order details
2. Click "Reset" to return status to Pending
   - Use if delivery was marked incorrectly
   - Confirms before resetting

<strong>For Cancelled Orders:</strong>
- Click "Reset to Pending" to restore order status
- Use if cancellation was recorded in error

<strong>Additional Features:</strong>
- Click "Refresh" to reload latest delivery data
- Click "Export CSV" to download report as spreadsheet
- Summary row shows count of Pending/Delivered/Cancelled orders
- System entries (inventory adjustments) show "System Entry" with no actions
- All status changes require confirmation to prevent mistakes

<strong>Document Numbers:</strong>
- Each delivery shows its invoice or document number
- Click the copy icon to copy document number
- Use for tracking and reference purposes`,
  image: "/manual-images/delivery.png",
},
          {
  id: 26,
  title: "Check Inventory",
  description: `The Current Inventory page displays all inventory items with their current stock levels and allows you to manage inventory details.

<strong>Viewing Inventory:</strong>
- Table shows: Product ID, Item Name, Description, Category, Price, Current Quantity, Status
- <strong>Stock Level Indicators:</strong>
  - Green badge: Good stock (more than 10 units)
  - Yellow badge: Low stock (6-10 units)
  - Red badge: Critical stock (5 or fewer units)
  - Red row highlight: Items with 5 or fewer units
- Click "Refresh" to reload latest inventory data

<strong>Search and Filter:</strong>
- Use search bar to find items by Product ID, name, or description
- Filter by Category using the dropdown
- Click "Reset" to clear all filters
- Use column filters for additional sorting and filtering

<strong>Adjusting Quantity:</strong>
1. Click "Adjust" button next to any item
2. Use +/- buttons or type a number to adjust quantity
   - Positive numbers add stock (e.g., +10)
   - Negative numbers remove stock (e.g., -5)
3. Preview shows current quantity and what new total will be
4. Click "Update Quantity" to save
5. All adjustments are logged as "Admin Adjustment"

<strong>Editing Item Details:</strong>
1. Click "Edit" button next to any item
2. Modify available fields:
   - Product ID
   - Item Name
   - Description
   - Category
3. Price per Unit is view-only (cannot be edited here)
4. Click "Save Changes" to update
5. Click "Cancel" to discard changes

<strong>Managing Status:</strong>
- Each item has Active or Inactive status
- Click the status dropdown to change:
  - <strong>Active</strong>: Item appears in POS for sales
  - <strong>Inactive</strong>: Item hidden from POS (discontinued/unavailable)
- Status change requires confirmation
- Use Inactive for items no longer sold but kept for records

<strong>Important Notes:</strong>
- Quantity adjustments are permanent and logged in the system
- Cannot adjust quantity to negative numbers
- Low stock items (≤5 units) are highlighted in red
- Status changes are immediate after confirmation
- All changes are tracked with admin information`,
  image: "/manual-images/checkinventory.png",
},
          {
  id: 27,
  title: "Sales Report",
  description: `The Sales Report provides a comprehensive overview of all sales transactions with filtering, search, and export capabilities.

<strong>Sales Overview Dashboard:</strong>
- <strong>Total Transactions</strong>: Count of all sales in the selected period
- <strong>Total Revenue</strong>: Sum of all final amounts collected
- <strong>Average Sale</strong>: Average transaction value
- <strong>Total VAT</strong>: Total VAT collected from all sales

All statistics update automatically based on applied filters.

<strong>Viewing Transaction Details:</strong>
The table displays:
- Transaction ID and Date/Time
- Receipt/Document Number (clickable to view full receipt)
- Student Name
- Items Purchased (shows first 2 items, hover for full list)
- Total, Discount, VAT amounts
- Final Amount (highlighted in bold)
- Payment Method (Cash, Bank Transfer, Credit Card, Online Payment)
- Bank Details (for bank transfer payments only)
- Created By (admin who processed the sale)

<strong>Search and Filter:</strong>
1. <strong>Date Range</strong>: Select "From" and "To" dates to filter by period
2. <strong>Search</strong>: Enter keywords to search across all transaction fields
3. Click "Apply" to activate filters
4. Click "Clear" to reset all filters
5. Click "Refresh" to reload latest data

<strong>Viewing Receipts:</strong>
- Click on any Document Number to view the full receipt
- Receipt shows complete transaction details
- Can be printed or saved as PDF

<strong>Exporting Data:</strong>
- Click "Export CSV" to download filtered transactions
- Filename includes date range (e.g., sales_report_20250101_to_20250131.csv)
- CSV contains all transaction details for further analysis

<strong>Deleting Transactions:</strong>
1. Click the red delete button (trash icon) on any transaction
2. Confirm deletion in the modal dialog
3. Enter your admin password to authorize deletion
4. Click "Delete" to proceed
5. <strong>Important</strong>: Deleted transactions:
   - Cannot be recovered
   - Return inventory items back to stock
   - Remove the transaction from all reports

<strong>Payment Method Display:</strong>
- Cash: Green tag
- Bank Transfer: Blue tag (includes bank account details)
- Credit Card: Purple tag
- Online Payment: Orange tag

<strong>Sorting and Filtering:</strong>
- Click column headers to sort data
- Use column filters for specific criteria
- Default sort is by date (newest first)
- Sort by Final Amount to see highest/lowest sales

<strong>Important Notes:</strong>
- Transaction deletion requires password confirmation
- Deleted transactions restore inventory quantities
- Export CSV uses current filter settings
- Receipt numbers are clickable links to view details
- Bank details only show for bank transfer payments`,
  image: "/manual-images/salesreport.png",
}
        ],
      },
      {
        section: "Attendance",
        topics: [
          {
  id: 28,
  title: "Attendance",
  description: `The Attendance page displays all student attendance records with comprehensive filtering and search capabilities.

<strong>Viewing Attendance Records:</strong>
The table displays:
- Branch (color-coded tags)
- Date of class
- Student Name (with nickname in parentheses)
- Course/Class Code
- Class ID
- Subject Name
- Teacher Name
- Class Type (Private or Group)
- Attendance Status (Present/Absent/Late)
- Homework Status (YES/NO/N/A)
- Teacher Comments
- Record Created Timestamp
- Last Edited Timestamp

<strong>Search and Filter Options:</strong>
1. <strong>Branch Filter</strong> (Superadmin only): Select specific branch to view
2. <strong>Student Name Search</strong>: Enter student name to find their records
3. <strong>Course Search</strong>: Enter course code to find class records
4. <strong>Date Range</strong>: Select start and end dates using the date picker
5. All filters work together for precise results

<strong>Status Indicators:</strong>
- <strong>Attendance:</strong>
  - Green tag: Present
  - Red tag: Absent
  - Orange tag: Late
- <strong>Homework:</strong>
  - Green tag: YES (completed)
  - Red tag: NO (not completed)
  - Gray tag: N/A (not assigned)

<strong>Managing Records:</strong>
- Click the red delete button to remove an attendance record
- Confirmation prompt appears before deletion
- Deleted records are permanently removed

<strong>Exporting Data:</strong>
- Click "Export CSV" to download all filtered attendance records
- CSV includes all columns displayed in the table
- Filename: attendance_data.csv
- Use for reporting, analysis, or record-keeping

<strong>Additional Features:</strong>
- Comments column displays full teacher feedback
- Timestamp shows when record was initially created
- Last Edited shows most recent modification time
- Table supports pagination (50 records per page by default)
- Scroll horizontally to view all columns
- Page size can be changed using size selector

<strong>Important Notes:</strong>
- Attendance records are created by teachers after each class
- Only administrators can delete attendance records
- Branch filter only appears for superadmin users
- All searches are real-time and case-insensitive
- Date range filter applies to the class date, not record creation date`,
  image: "/manual-images/attendance.png",
},
          {
  id: 29,
  title: "Student Feedback",
  description: `The Student Feedback page displays detailed teacher feedback for each student after every class session.

<strong>Viewing Feedback Records:</strong>
The table displays:
- Branch (color-coded)
- Class ID and Class Code
- Subject Name
- Date, Time, and Duration of class
- Teacher Name (with nickname)
- Student Name (with nickname)
- Approval Status (Approved/Pending)
- Six feedback categories:
  - Attendance & Punctuality
  - Class Participation
  - Comprehension
  - Focus & Behavior
  - Progress Since Last Class
  - Areas to Improve
- Teacher's Comments
- Created By, Updated By, and Approved By information
- Unique Feedback Reference ID

<strong>Search and Filter Options:</strong>
1. <strong>Branch Filter</strong> (Superadmin only): Select specific branch
2. <strong>Student Name Search</strong>: Find feedback by student name
3. <strong>Feedback Ref Search</strong>: Search by unique feedback reference ID
4. <strong>Date Range</strong>: Select start and end dates
5. All filters work together for precise results
6. Click "Refresh" to reload latest data

<strong>Managing Feedback:</strong>

<strong>Editing Feedback:</strong>
1. Click the edit icon (pencil) next to any record
2. Drawer opens with all feedback fields
3. Modify any of the six feedback categories or comments
4. Click "Save" to update
5. <strong>Important</strong>: Only pending feedback can be edited
6. Approved feedback displays as view-only

<strong>Approving Feedback:</strong>
1. Click the checkmark icon to approve/unapprove
2. Confirmation dialog appears
3. Status toggles between:
   - <strong>Pending</strong> (Orange): Can be edited
   - <strong>Approved</strong> (Green): Locked from editing
4. Approved feedback cannot be edited unless unapproved first
5. System records who approved and when

<strong>Printing Feedback:</strong>
1. Click the printer icon next to any record
2. Print modal opens with formatted feedback
3. Contains all class, teacher, and student details
4. Includes all six feedback categories
5. Can be printed or saved as PDF
6. Use for parent-teacher meetings or records

<strong>Approval Status:</strong>
- <strong>Pending</strong> (Orange tag): Feedback is draft, can be edited
- <strong>Approved</strong> (Green tag): Feedback is final, locked from changes

<strong>Exporting Data:</strong>
- Click "Export CSV" to download all filtered feedback
- Includes all feedback categories and metadata
- Filename: student_feedback_report.csv
- Useful for analysis and reporting

<strong>Feedback Categories Explained:</strong>
1. <strong>Attendance & Punctuality</strong>: Student's arrival and presence
2. <strong>Class Participation</strong>: Engagement and involvement in class
3. <strong>Comprehension</strong>: Understanding of lesson materials
4. <strong>Focus & Behavior</strong>: Attention span and conduct
5. <strong>Progress Since Last Class</strong>: Improvement and development
6. <strong>Areas to Improve</strong>: Specific recommendations for growth

<strong>Important Notes:</strong>
- Feedback is created by teachers after each class
- Each feedback has a unique reference ID for tracking
- Only pending feedback can be modified
- Approval locks feedback to prevent changes
- System tracks all updates and approvals with timestamps
- Branch filter only available for superadmin users
- Edit drawer shows approval status at the top
- Save button is disabled for approved feedback`,
  image: "/manual-images/feedback.png",
},
          {
  id: 30,
  title: "Leave Request",
  description: `The Leave & Pause Management system handles student requests to reschedule classes or temporarily pause their courses.

<strong>Two Types of Requests:</strong>

<strong>1. Leave Requests</strong> - Students requesting to reschedule a specific class
<strong>2. Pause Requests</strong> - Students requesting to temporarily pause their entire course

<strong>Viewing Requests:</strong>

<strong>Leave Requests Tab:</strong>
- Branch (color-coded)
- Request Date
- Student Information (name, nickname, email, phone)
- Course/Class Code and Subject
- Original Schedule (date and time)
- Preferred Schedule (date and time)
- Reason for request
- Status (Pending/Approved/Rejected)
- Approved By (admin name)
- Admin Notes

<strong>Pause Requests Tab:</strong>
- Branch (color-coded)
- Request Date
- Student Information
- Course Code
- Pause Type (temporary, medical, etc.)
- Planned Duration
- Resume Date
- Reason for pause
- Status (Pending/Approved/Rejected)
- Approved By (admin name)
- Admin Notes

<strong>Search and Filter:</strong>
1. <strong>Branch Filter</strong> (Superadmin only): Select specific branch
2. <strong>Student Search</strong>: Search by student name or nickname
3. <strong>Status Filter</strong>: Filter by All/Pending/Approved/Rejected
4. Summary shows Total requests and Pending count
5. Each tab has a badge showing pending request count

<strong>Managing Requests:</strong>
<img src="/manual-images/admin-leave2.png" alt="Leave Request Review" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Review Process:</strong>
1. Click "Review" button on any request
2. Modal opens showing:
   - Student contact information
   - Current status with color coding
   - Complete request details
   - Original and preferred schedules (for leave)
   - Pause type and duration (for pause)
   - Student's reason
   - Request submission date

<strong>Approving/Rejecting:</strong>
1. In the review modal, select new Status:
   - <strong>Pending</strong> (Orange): Under review
   - <strong>Approved</strong> (Green): Request accepted
   - <strong>Rejected</strong> (Red): Request denied
2. Add Admin Notes explaining the decision
3. Click "Update Request" to save
4. System records who approved/rejected and when
5. Student receives notification of decision

<strong>Status Indicators:</strong>
- <strong>Pending</strong> (Orange, hourglass icon): Awaiting admin action
- <strong>Approved</strong> (Green, checkmark icon): Request accepted
- <strong>Rejected</strong> (Red, X icon): Request denied

<strong>Important Notes:</strong>
- Pending requests appear with badge counts on tabs
- Branch filter only available for superadmin users
- All status changes are logged with admin information
- Students can see their request status in their portal
- Admin notes are visible to other admins but not students
- Requests are sorted by submission date (newest first)
- Both tabs support independent search and filtering
- Table shows approved_by name after status change

<strong>Best Practices:</strong>
- Review requests promptly to help students plan
- Add clear admin notes explaining decisions
- For leave requests, consider teacher availability
- For pause requests, verify resume date is reasonable
- Check student's attendance history before deciding
- Communicate complex decisions directly with students`,
  image: "/manual-images/admin-leave.png",
},

          {
  id: 31,
  title: "Course End Reminder",
  description: `The Class Registration Reminders system helps identify students nearing course completion and sends automated reminder emails to encourage re-enrollment.

<strong>Viewing Student Status:</strong>
The table displays:
- Branch (color-coded)
- Student Information (name, nickname, email)
- Enrolled Courses with details:
  - Class Code and Subject Name
  - Teacher Name
  - Remaining classes shown (max 4 per course)
  - Next class date
- Urgency Level with color-coded indicators:
  - <strong>Critical (Red)</strong>: Only 1 class remaining
  - <strong>Urgent (Orange)</strong>: 2 classes remaining
  - <strong>Soon (Yellow)</strong>: 3-4 classes remaining
  - <strong>Good (Green)</strong>: More than 4 classes
- Total classes shown across all courses
- Send Reminder button status
- Notes from previous reminders

<strong>Search and Filter:</strong>
1. <strong>Branch Filter</strong> (Superadmin only): Select specific branch
2. <strong>Search Bar</strong>: Search by student name, nickname, email, class code, or subject
3. Click "Refresh" to reload latest data
4. Table shows real-time urgency levels

<strong>Viewing Class Details:</strong>
1. Click the expand arrow (▶) on any student row
2. Expanded view shows:
   - Complete schedule for each enrolled course
   - Next 4 upcoming classes (or fewer if less available)
   - Dates and times for each class
   - First class highlighted in blue
   - Last shown class marked
   - Total summary across all courses
3. Each course displayed in separate cards
<img src="/manual-images/reminder2.png" alt="Send Reminder Email" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Sending Reminder Emails:</strong>
1. Click "Send Reminder" button for any student
2. Email preview modal opens showing:
   - Student contact information
   - Total remaining classes
   - All enrolled courses with details
   - Next class and final shown class dates
   - Auto-generated professional email template
3. Review the pre-filled message
4. Click "Send Email" to deliver
5. Email sent to student with CC to noreply@keaes.com
6. Button changes to "Sent ✓" with green styling
7. System tracks when email was sent

<strong>IMPORTANT: Email must be sent before notes can be added</strong>
<img src="/manual-images/reminder3.png" alt="Add Note" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Managing Notes:</strong>
<strong>⚠️ CRITICAL: You MUST send a reminder email first before you can add or edit notes.</strong>

1. Notes are only available AFTER sending a reminder email
2. Once email is sent, "Add Note" button appears in Notes column
3. Click "Add Note" or "Edit Note" to open note editor
4. Enter relevant information such as:
   - Student's response to reminder
   - Re-enrollment status
   - Follow-up required
   - Special circumstances
5. Click "Save" to store note
6. Notes visible to all admins for continuity
7. Latest note displays in table

<strong>Workflow Summary:</strong>
1. First: Send reminder email (required)
2. Then: Add notes to track follow-up (optional)
3. Notes cannot be added without sending email first

<strong>Email Tracking:</strong>
- Button shows "Sent ✓" if reminder sent recently
- System prevents duplicate emails within timeframe
- All sent emails logged with timestamp
- Email status updates automatically on refresh
- Note functionality unlocks after email is sent

<strong>Urgency Indicators Explained:</strong>
- <strong>Critical (1 class)</strong>: Immediate action needed
- <strong>Urgent (2 classes)</strong>: Contact within 1-2 days
- <strong>Soon (3-4 classes)</strong>: Plan follow-up contact
- <strong>Good (5+ classes)</strong>: Monitor periodically

<strong>Auto-Generated Email Template:</strong>
The system creates professional emails including:
- Personalized greeting with student name
- Total remaining classes count
- List of all enrolled courses with:
  - Course code and subject
  - Number of classes remaining
  - Next class date
  - Final shown class date
- Call to action for re-enrollment
- KEAES contact information
- Professional closing

<strong>Important Notes:</strong>
- <strong>Email MUST be sent before adding notes - this is mandatory</strong>
- System shows maximum 4 upcoming classes per course
- "Remaining classes" means classes currently visible in system
- Students may have more classes if enrolled beyond shown dates
- Email sent status persists until student re-enrolls
- Critical urgency students should be prioritized
- Branch filter only available for superadmin users
- All reminder emails copied to noreply@keaes.com
- Notes help track follow-up conversations after email is sent

<strong>Best Practices:</strong>
- Send reminders when urgency reaches "Urgent" or "Critical"
- Add notes immediately after sending email to document action
- Add follow-up notes after every student contact
- Review critical urgency students daily
- Personalize follow-up based on student history
- Check expanded view for complete class schedule
- Coordinate with sales team for re-enrollment offers
- Track patterns in student re-enrollment behavior`,
  image: "/manual-images/reminder1.png",
},
          {
  id: 32,
  title: "Completed Student Tracker",
  description: `The Completed Students Tracker helps monitor students who have finished their courses and manages follow-up activities to encourage re-enrollment.

<strong>Dashboard Statistics:</strong>
- <strong>Total Completed</strong>: All students who completed courses in selected date range
- <strong>This Week</strong>: Students who completed within last 7 days
- <strong>This Month</strong>: Students who completed within last 30 days
- <strong>Needs Follow-up</strong>: Students completed more than 30 days ago (high priority)

<strong>Viewing Completed Students:</strong>
The table displays:
- Branch (color-coded)
- Last Class Date with:
  - Formatted date
  - Days since completion
  - Class time
- Student Information:
  - Full name with nickname
  - Email and phone number
  - Note count badge (blue for regular, red if important notes exist)
- Last Completed Course (class code and teacher)
- Follow-up Priority with color coding:
  - <strong>Fresh (Green)</strong>: 0-7 days - Low Priority
  - <strong>Recent (Blue)</strong>: 8-30 days - Medium Priority
  - <strong>Warm (Orange)</strong>: 31-90 days - High Priority
  - <strong>Cold (Red)</strong>: 90+ days - Very High Priority
- Actions: Notes button showing count

<strong>Search and Filter:</strong>
1. <strong>Date Range Picker</strong>: Select completion date range (defaults to current month)
2. <strong>Branch Filter</strong> (Superadmin only): Filter by specific branch
3. <strong>Search Bar</strong>: Search by student name, nickname, email, course code, or subject
4. Click "Refresh" to reload data with current filters

<strong>Managing Student Notes:</strong>

<strong>Adding Notes:</strong>
1. Click "Notes" button for any student
2. Modal opens showing student details and note history
3. Scroll to "Add New Note" form at bottom
4. Enter note text (required)
5. Select Note Type:
   - <strong>General</strong>: Regular notes
   - <strong>Follow Up</strong>: Scheduled follow-up tasks
   - <strong>Contact Attempt</strong>: Call/email attempts
   - <strong>Feedback</strong>: Student feedback received
   - <strong>Complaint</strong>: Issues or complaints
   - <strong>Referral</strong>: Referral information
6. Check "Mark as Important" for critical notes
7. Click "Add Note" to save
8. Note appears in history with timestamp and your name

<strong>Editing Notes:</strong>
1. Click "Edit" button on any note
2. Note content loads into form
3. Modify text, type, or importance
4. Click "Update Note" to save changes
5. Click "Cancel Edit" to discard changes

<strong>Deleting Notes:</strong>
1. Click "Delete" button on any note
2. Confirmation prompt appears
3. Click "Yes" to permanently delete
4. Note removed from history immediately

<strong>Note Types and Colors:</strong>
- <strong>General</strong> (Default): Standard notes
- <strong>Follow Up</strong> (Blue): Action required
- <strong>Contact Attempt</strong> (Orange): Communication attempts
- <strong>Feedback</strong> (Green): Student feedback
- <strong>Complaint</strong> (Red): Issues to address
- <strong>Referral</strong> (Purple): Referral tracking

<strong>Important Notes Badge:</strong>
- Red "IMPORTANT" tag appears on flagged notes
- Important note count shows in red on student row
- Use for urgent follow-ups or critical information

<strong>Expandable Row Details:</strong>
Click expand arrow (▶) to view:
- Complete student contact information
- Last course details (code, subject, teacher)
- Exact completion date and time
- Days since completion with urgency tag

<strong>Notes Modal Features:</strong>
- <strong>Student Info Card</strong>: Quick reference (name, email, phone, last course)
- <strong>Notes History</strong>: Chronological list of all notes
- <strong>Note Metadata</strong>: Shows who created note and when
- <strong>Add/Edit Form</strong>: Create new or modify existing notes
- <strong>Real-time Updates</strong>: Changes reflect immediately in table

<strong>Best Practices:</strong>
- Add notes immediately after student contact
- Use specific note types for better tracking
- Mark important notes for priority follow-ups
- Review "Cold" (red) priority students weekly
- Check "Needs Follow-up" statistic regularly
- Document all contact attempts with date/time
- Track re-enrollment progress in notes
- Use "Follow Up" type with action date in note text

<strong>Follow-up Priority Guidelines:</strong>
- <strong>Fresh (0-7 days)</strong>: 
  - Recently completed, may be processing experience
  - Low pressure contact appropriate
- <strong>Recent (8-30 days)</strong>: 
  - Good timing for re-enrollment discussion
  - Student still engaged with material
- <strong>Warm (31-90 days)</strong>: 
  - Risk of disengagement increasing
  - Active follow-up recommended
- <strong>Cold (90+ days)</strong>: 
  - High risk of losing student
  - Immediate action required
  - May need special offers or incentives

<strong>Important Notes:</strong>
- Date range defaults to current month on page load
- Branch filter only available for superadmin users
- Note counts update automatically after adding/editing
- Important notes highlighted in red throughout system
- All notes show creator name and timestamp
- Students with no notes show "No notes" text
- Delete action requires confirmation to prevent accidents
- Edit mode cancels automatically when adding new note
- Search filters apply in real-time
- Priority colors help visualize urgency at a glance`,
  image: "/manual/admin-completed-tracker.png",
},
          {
  id: 33,
  title: "Teacher Rating",
  description: `The Teacher Rating report displays all student feedback and ratings given to teachers after class sessions.

<strong>Viewing Teacher Ratings:</strong>
The table displays:
- Branch (color-coded)
- Teacher Name (nickname)
- Class Code
- Subject Name
- Class Date
- Student Name (who gave the rating)
- Rating Score (numerical rating)
- Comments (student's written feedback)
- Timestamp (when rating was submitted)

<strong>Search and Filter:</strong>
1. <strong>Branch Filter</strong> (Superadmin only): Select specific branch to view
2. <strong>Teacher Name Search</strong>: Enter teacher name to find their ratings
3. <strong>Date Range Picker</strong>: Select start and end dates to filter by class date
4. All filters work together for precise results
5. Table updates automatically when filters change

<strong>Exporting Data:</strong>
- Click "Export CSV" to download all filtered ratings
- CSV includes all columns shown in the table
- Filename: teacher_rating_report.csv
- Use for performance reviews and analysis

<strong>Understanding Ratings:</strong>
- Ratings are submitted by students after each class
- Students rate teacher performance numerically
- Comments provide qualitative feedback
- All ratings are timestamped for tracking
- Both rating score and comments are displayed

<strong>Using the Report:</strong>
- Review individual teacher performance
- Identify patterns in student feedback
- Track ratings over time periods
- Compare ratings across different subjects
- Use for teacher evaluations and improvements
- Export data for detailed analysis

<strong>Table Features:</strong>
- Sortable columns for easy organization
- Horizontal scroll for all columns
- 50 records per page (adjustable)
- Page size can be changed using selector
- Quick jump to specific pages available

<strong>Important Notes:</strong>
- Branch filter only available for superadmin users
- All student feedback is visible to administrators
- Ratings help identify teaching strengths and areas for improvement
- Date filter applies to the class date, not rating submission date
- Comments field may be empty if student didn't provide written feedback
- Search is case-insensitive for teacher names
- Export includes only filtered results, not all ratings

<strong>Best Practices:</strong>
- Review ratings regularly to monitor teacher performance
- Look for consistent patterns in feedback
- Address any concerning comments promptly
- Use ratings as part of comprehensive teacher evaluation
- Share positive feedback with teachers for motivation
- Filter by date range for periodic reviews (monthly/quarterly)
- Compare ratings across teachers for the same subject
- Consider both rating scores and written comments for full context`,
  image: "/manual-images/teacher-rating.png",
},
          {
  id: 34,
  title: "Student Test Scores",
  description: `The Student Test Scores page allows you to record and track standardized test results for all students.

<strong>Viewing Test Scores:</strong>
The table displays:
- Branch (color-coded)
- Student Full Name
- Nickname
- Test Date
- Test Type (e.g., SAT, IELTS, GED, IGCSE)
- Subject/Subtype
- Details (additional test information)
- Score
- Notes
- Actions (Delete button)

<strong>Adding New Test Scores:</strong>
1. Click "Add Student Score" button at top right
2. Modal form opens with required fields:
   - <strong>Student</strong>: Search and select from dropdown
   - <strong>Test Date</strong>: Select date from calendar picker
   - <strong>Test Type</strong>: Choose from available types:
     * GED, IGCSE, A-LEVEL, SAT, IELTS, AP, IB, TOEFL
     * TOEFL-MUIC, CU-AAT, CU-ATS, CU-TEP, TU-GET, OTHERS
   - <strong>Subject</strong>: Appears after selecting test type
     * Each test type has specific subjects available
     * Subject list updates based on selected test type
   - <strong>Details</strong>: Optional additional information (can be left blank)
   - <strong>Score</strong>: Enter the test score
   - <strong>Note</strong>: Optional notes about the test result
3. Click "OK" to save the score
4. New entry appears in the table immediately

<strong>Search and Filter Options:</strong>
1. <strong>Branch Filter</strong> (Superadmin only): Select specific branch
2. <strong>Search Bar</strong>: Search by student name or nickname
3. <strong>Date Range Picker</strong>: Filter by test date (start and end dates)
4. <strong>Test Type Filter</strong>: Select specific test type to view
5. All filters work together for precise results
6. Filters update table in real-time

<strong>Available Test Types:</strong>
- GED
- IGCSE
- A-LEVEL
- SAT
- IELTS
- AP (Advanced Placement)
- IB (International Baccalaureate)
- TOEFL
- TOEFL-MUIC
- CU-AAT
- CU-ATS
- CU-TEP
- TU-GET
- OTHERS (for any unlisted test)

<strong>Deleting Test Scores:</strong>
1. Click "Delete" button for any test score entry
2. Confirmation dialog appears
3. Confirm with "Yes, Delete" to remove permanently
4. Action cannot be undone
5. Entry removed from table immediately

<strong>Important Notes:</strong>
- Test Date is required for all entries
- Student selection is searchable for easy finding
- Subject options change based on selected test type
- Details field is optional and can be left empty
- Branch filter only available for superadmin users
- All test scores are permanently stored
- Delete action requires confirmation to prevent accidents
- Date format displays as DD MMM YY (e.g., "15 JAN 25")
- Table pagination shows 100 records per page

<strong>Best Practices:</strong>
- Record test scores immediately after receiving results
- Use Notes field for context (e.g., "First attempt", "Retake")
- Include test date accurately for tracking progress
- Use Details field for specific test version or session info
- Keep records updated for college applications
- Filter by test type to view student progress in specific exams
- Use date range to track scores over academic periods
- Verify student selection before saving to avoid errors`,
  image: "/manual-images/student-testscore.png",
},
        ],
      },
     {
  section: "Resources",
  topics: [
    {
      id: 35,
      title: "Managing Resources",
      description: `The Resource Management page allows you to organize educational materials for your students.

<strong>Adding Resources:</strong>
1. Click "Add Resource" button
2. Select Category and Subject
3. Enter Title and Description
4. Choose Resource Type:
   • PDF Upload - Upload files (max 10MB)
   • Video Link - YouTube or Vimeo URLs
   • File Link - External file URLs
5. Upload file or enter URL
6. Click "Create"

<strong>Filtering & Search:</strong>
- Use the search bar to find resources by title
- Filter by Category, Subject, or Type
- Click "Refresh" to reload resources

<strong>Managing Resources:</strong>
- Click "View/Download" to access the resource
- Click "Edit" to modify details or replace files
- Click "Delete" to remove (requires confirmation)

Each resource displays as a card with thumbnail, title, description, and category/subject tags.`,
      image: "/manual-images/resource1.png",
    },
    {
      id: 36,
      title: "Creating Categories",
      description: `Categories are top-level groupings for organizing resources (e.g., Mathematics, Science, Languages).

<strong>To Add a Category:</strong>
1. Click "Add Category" button on the main page
2. Enter a clear category name (minimum 2 characters)
3. Review existing categories shown in the preview section
4. Click "Create Category"

<strong>Best Practices:</strong>
- Use broad, general subjects as categories
- Keep names consistent and professional
- Avoid creating duplicate categories
- Categories should represent major subject areas

Once created, categories appear in the dropdown when adding new resources and can be used as filters on the main page.`,
      image: "/manual-images/resource2.png",
    },
    {
      id: 37,
      title: "Creating Subjects",
      description: `Subjects are specific topics within categories (e.g., Algebra under Mathematics, Biology under Science).

<strong>To Add a Subject:</strong>
1. Click "Add Subject" button on the main page
2. Enter a descriptive subject name (minimum 2 characters)
3. Review existing subjects in the preview section
4. Click "Create Subject"

<strong>Best Practices:</strong>
- Subjects should be specific topics within your categories
- Use clear, recognizable names
- Create subjects that align with your curriculum
- Avoid overly broad or duplicate subject names

Subjects help students find relevant resources quickly and organize materials by specific learning topics.`,
      image: "/manual-images/resource3.png",
    },
  ],
}
    ],
  },
  {
    category: "Superadmin",
    sections: [
      {
        section: "Accounting",
        topics: [
          {
  id: 38,
  title: "Invoice",
  description: `The Invoice page displays all student registration invoices with payment tracking and management capabilities.

<strong>Viewing Invoices:</strong>
The table displays:
- Invoice Number (clickable to view full invoice)
- Branch (color-coded tag)
- Date of registration
- Student Name
- Classes enrolled:
  - Class codes displayed
  - Tag showing count (e.g., "2 classes")
  - Total hours shown
- Class Price (before discounts)
- Discount amount
- VAT amount
- Total (final price in bold blue)
- Remaining Balance (in red if unpaid)
- Pay button (disabled if fully paid)
- Created By (admin who created invoice)
- Delete button (red trash icon)

<strong>Search and Filter Options:</strong>
1. <strong>Branch Filter:</strong> Select specific branch or All Branches
2. <strong>Date Range Picker:</strong> Filter by registration date (defaults to current month)
3. <strong>Search Bar:</strong> Search by invoice number, student name, or class codes
4. <strong>Admin Filter:</strong> Filter by admin who created the invoice
5. <strong>Balance Filter:</strong> 
   - "Has remaining balance" - Shows unpaid/partially paid invoices
   - "Overdue" - Shows fully paid invoices
6. Click "Clear Filter" to reset all filters
7. Click "Refresh" to reload latest data

<strong>Viewing Full Invoice:</strong>
1. Click on any Invoice Number (blue link)
2. Full invoice modal opens showing:
   - Complete student information
   - All enrolled classes with details
   - Itemized pricing breakdown
   - Payment history if any
   - Total and remaining balance
3. Invoice can be printed or saved as PDF

<strong>Making Payments:</strong>
1. Click Pay button ($) for any invoice with remaining balance
2. Payment modal opens showing:
   - Invoice number and student name
   - Classes enrolled and count
   - Total amount and remaining balance
3. Enter Payment Amount:
   - Cannot exceed remaining balance
   - System shows maximum allowed amount
4. Select Payment Method:
   - Cash
   - Credit Card
   - Bank Transfer
   - Online Payment
5. <strong>For Bank Transfer only:</strong>
   - Additional field appears to select receiving bank account
   - Choose from available bank accounts
   - Default bank auto-selected if configured
6. Add optional Memo notes
7. Click "OK" to process payment
8. System updates remaining balance immediately
9. Pay button becomes disabled when fully paid

<strong>Deleting Invoices:</strong>
1. Click Delete button (red trash icon)
2. Confirmation modal appears showing invoice number
3. <strong>Security:</strong> Enter your password to confirm deletion
4. Click "Delete" to proceed
5. <strong>Important Restrictions:</strong>
   - Cannot delete if payments have been recorded
   - Cannot delete if attendance has been recorded
   - Error message displays if deletion blocked
6. Invoice removed from table if deletion successful
7. <strong>Warning:</strong> This action cannot be undone

<strong>Exporting Data:</strong>
- Click "Export CSV" button to download filtered invoices
- Filename includes current date
- CSV includes all table columns plus totals row:
  - Branch, Invoice No., Date, Student
  - Classes, Class Count
  - Class Price, Discount, VAT, Total
  - Remaining Balance
  - Created By admin
- Totals row summarizes all financial columns

<strong>Invoice Details:</strong>
- <strong>Single Class Registration:</strong> Green tag, shows 1 class
- <strong>Batch Registration:</strong> Blue tag, shows multiple classes count
- <strong>Total Hours:</strong> Sum of all class hours
- <strong>Remaining Balance:</strong> 
  - Shown in red with parentheses if unpaid
  - Shows "0" when fully paid
- <strong>Admin Display:</strong> Shows admin nickname or "Admin [ID]"

<strong>Payment Method Notes:</strong>
- <strong>Cash:</strong> No additional information required
- <strong>Credit Card:</strong> No additional information required
- <strong>Bank Transfer:</strong> Must select receiving bank account
- <strong>Online Payment:</strong> No additional information required

<strong>Important Notes:</strong>
- Only superadmin can access this page
- Date range defaults to current month
- Invoice numbers have format "INV######"
- Table shows 50 invoices per page (adjustable)
- Pay button disabled automatically when fully paid
- Password required for all deletions for security
- Cannot delete invoices with associated payments or attendance
- Remaining balance calculated automatically
- VAT and discounts reflected in total price
- All amounts displayed with 2 decimal places
- Branch filter shows all branches with invoices

<strong>Best Practices:</strong>
- Review remaining balances regularly
- Use balance filter to find unpaid invoices
- Add memo notes for payment tracking
- Export CSV for monthly financial reports
- Verify payment method before confirming
- Double-check bank account for transfers
- Clear filters when switching between views
- Use date range for monthly reconciliation
- Check admin filter to track who creates invoices`,
  image: "/manual-images/invoice.png",
},
          {
  id: 39,
  title: "Payment",
  description: `The Payment page displays all payment transactions with detailed filtering and tracking capabilities.

<strong>Viewing Payments:</strong>
The table displays:
- Branch (color-coded tag)
- Confirm Paid Date (timestamp when payment confirmed)
- Receipt Number (clickable to view full receipt)
- Reference Invoice Number
- Student Name
- Classes:
  - Class codes displayed
  - Class count tag (for multiple classes)
  - Hover icon to see price breakdown per class
- Payment Method (color-coded):
  - Cash (Green)
  - Credit Card (Blue)
  - Bank Transfer (Purple)
  - Online Payment (Orange)
- Bank Details (for bank transfers only):
  - Bank name
  - Account number
  - Account holder name
  - Branch (if available)
- Total Price (original invoice amount)
- Paid Amount (payment received, in green bold)
- Remaining Balance (red if unpaid, green if zero)
- Memo notes
- Delete button (red trash icon)

<strong>Search and Filter Options:</strong>
1. <strong>Branch Filter:</strong> Select specific branch or view all
2. <strong>Date Range Picker:</strong> Filter by payment confirmation date (defaults to current month)
3. <strong>Search Bar:</strong> Search by invoice number, student name, or class codes
4. <strong>Payment Method Filter:</strong> Filter by Cash/Credit Card/Bank Transfer/Online Payment
5. <strong>Bank Filter:</strong> Filter by specific bank (for bank transfers)
6. Click "Clear Filter" to reset all filters
7. Click "Refresh" to reload latest data

<strong>Date Range Behavior:</strong>
- Defaults to current month on page load
- Clear both dates to view all payments without date filter
- Selecting new date range automatically fetches data
- Date range displayed in summary section when active

<strong>Viewing Receipt:</strong>
1. Click on any Receipt Number (blue link)
2. Full receipt modal opens showing:
   - Complete payment information
   - Student details
   - All classes and pricing
   - Payment method and bank details
   - Running balance calculation
3. Receipt can be printed or saved as PDF

<strong>Bank Details Display:</strong>
- <strong>For Bank Transfers:</strong>
  - Shows complete bank information
  - Bank name in blue
  - Account number and holder name
  - Branch location if available
- <strong>For Other Methods:</strong>
  - Shows "N/A" in gray
  - Bank details not applicable
- <strong>Missing Bank Info:</strong>
  - Red "No Bank Info" tag appears
  - Indicates data not recorded during payment

<strong>Multiple Classes Information:</strong>
- Blue tag shows total class count
- Hover over info icon (ⓘ) to see:
  - Individual class codes
  - Price for each class
  - Breakdown of batch payment

<strong>Deleting Payments:</strong>
1. Click Delete button (red trash icon)
2. Confirmation dialog appears
3. Warning message about deletion
4. Click "Yes" to confirm deletion
5. Payment removed from system immediately
6. <strong>Warning:</strong> This action cannot be undone
7. Invoice remaining balance automatically recalculated

<strong>Summary Information:</strong>
- Displays count of filtered payments vs total
- Shows active date range when applied
- Updates in real-time with filters
- Located above the table for easy reference

<strong>Exporting Data:</strong>
- Click "Export CSV" button to download payments
- Filename includes current date
- CSV includes all table columns:
  - Branch and Branch ID
  - Paid timestamp, Invoice No., Receipt No.
  - Student name (first and last separate)
  - Classes, Class Count
  - Payment Method
  - Bank Name and Account (if applicable)
  - Subtotal, Paid amount, Remaining balance
  - Memo notes
- Only filtered results exported, not all payments

<strong>Payment Method Colors:</strong>
- <strong>Cash:</strong> Green tag
- <strong>Credit Card:</strong> Blue tag
- <strong>Bank Transfer:</strong> Purple tag
- <strong>Online Payment:</strong> Orange tag

<strong>Table Features:</strong>
- Sortable by confirm paid date (newest first by default)
- Shows 50 payments per page (adjustable)
- Quick jump to specific pages available
- Horizontal scroll for all columns
- Pagination info shows current range

<strong>Important Notes:</strong>
- Only superadmin can access this page
- Date range defaults to current month for performance
- Receipt numbers generated automatically for each payment
- Bank details only shown for bank transfer payments
- Remaining balance updates automatically after payment
- Memo field shows first 20 characters with hover tooltip
- Delete action is immediate and cannot be undone
- All amounts displayed with thousand separators (฿)
- Class count helps identify batch registrations
- Payment timestamp shows exact date and time

<strong>Best Practices:</strong>
- Use date range to focus on specific periods
- Export CSV for monthly reconciliation
- Verify bank details for bank transfer payments
- Review remaining balances regularly
- Add meaningful memo notes during payment
- Filter by payment method for accounting reports
- Check receipt after each payment confirmation
- Use branch filter for multi-branch tracking
- Clear filters when switching between views
- Confirm payment method matches bank details display`,
  image: "/manual-images/payment.png",
},
          {
  id: 40,
  title: "Add Expense",
  description: `The Add Expense page allows superadmin to record and track all business expenses with receipt attachments.

<strong>Adding New Expense:</strong>

<strong>Required Fields:</strong>
1. <strong>Branch:</strong> Select which branch incurred this expense
2. <strong>Date:</strong> Select the date expense was incurred (format: DD/MM/YYYY)
3. <strong>Amount:</strong> Enter expense amount in Baht (฿)
   - Automatically formats with thousand separators
   - Supports decimal values (e.g., 1,234.56)
4. <strong>Category:</strong> Select expense category from dropdown
5. <strong>Details:</strong> Enter specific description of the expense

<strong>Optional Fields:</strong>
- <strong>Memo:</strong> Add additional notes or context (text area)
- <strong>Upload Receipt:</strong> Attach receipt image or PDF

<strong>Adding New Category:</strong>
1. Click "add new category" link below category dropdown
2. Modal opens with category name field
3. Enter descriptive category name
4. Click "Add" to save
5. New category appears in dropdown immediately
6. Can be used for current and future expenses

<strong>Uploading Receipt:</strong>
1. Click "Upload" button
2. Select file from computer
3. <strong>Supported formats:</strong>
   - PDF documents (.pdf)
   - JPEG images (.jpg, .jpeg)
   - PNG images (.png)
4. Maximum 1 file per expense
5. File preview shows after upload
6. Click X icon to remove uploaded file
7. Can replace file by uploading new one

<strong>Form Actions:</strong>
- <strong>Reset:</strong> Clears all form fields
- <strong>Submit:</strong> Saves expense to database

<strong>After Submission:</strong>
1. Success message appears
2. Form automatically resets
3. Uploaded file removed
4. Ready for next expense entry
5. Expense immediately available in Expense Report

<strong>Amount Input Features:</strong>
- Prefix with ฿ symbol automatically
- Formats with commas for thousands
- Accepts decimal values (2 decimal places)
- Minimum value is 0
- Example: Entering "1234.56" displays as "฿ 1,234.56"

<strong>Date Selection:</strong>
- Calendar picker for easy selection
- Defaults to today's date
- Can select past or future dates
- Format: DD/MM/YYYY (e.g., 15/01/2025)
- System automatically extracts month-year for reporting

<strong>Important Notes:</strong>
- Only superadmin can access this page
- Branch selection is mandatory
- All expenses linked to specific branch
- Receipt upload is optional but recommended
- Categories are shared across all branches
- Custom categories available for unique expenses
- Amount must be positive number
- Date, amount, category, and details are required
- Memo field useful for additional context
- Receipt file stored securely on server
- Can add multiple expenses in succession

<strong>Best Practices:</strong>
- Upload receipts whenever possible for audit trail
- Use specific details rather than generic descriptions
- Select most appropriate category for accurate reporting
- Add memo notes for unusual expenses
- Verify branch selection before submitting
- Double-check amount before saving
- Create new categories for recurring unique expenses
- Use consistent naming for similar expenses
- Keep receipts organized by category
- Review expense before clicking submit

<strong>Form Validation:</strong>
- Red asterisk (*) indicates required fields
- Error messages appear for missing required fields
- Cannot submit until all required fields filled
- Amount must be valid number
- Date must be valid calendar date
- Category must be selected from dropdown
- Branch must be selected

<strong>Receipt File Guidelines:</strong>
- Clear, readable images preferred
- Ensure receipt details visible
- PDF format ideal for documents
- JPEG/PNG for photos of receipts
- File size should be reasonable
- One receipt per expense entry
- Multiple items on one receipt = one expense entry`,
  image: "/manual-images/addexpense.png",
},
          {
  id: 41,
  title: "Expense Report",
  description: `The Expense Report page displays all recorded expenses with comprehensive filtering, summaries, and editing capabilities.

<strong>Overall Expense Summary:</strong>
Dashboard displays four key metrics:
- <strong>Total Amount:</strong> Sum of all expenses in THB (blue)
- <strong>Number of Expenses:</strong> Total count of expense entries
- <strong>Most Common Category:</strong> Category with highest frequency
- <strong>Latest Expense Date:</strong> Most recent expense recorded

<strong>Branch Expense Summary:</strong>
Individual cards for each branch showing:
- Branch name (color-coded tag)
- <strong>Amount:</strong> Total expenses for that branch (green)
- <strong>Count:</strong> Number of expenses for that branch
- <strong>Top Category:</strong> Most common expense category (with color tag)
- <strong>Latest:</strong> Date of most recent expense

All summaries update automatically based on applied filters.

<strong>Viewing Expenses:</strong>
The table displays:
- Branch (color-coded tag)
- Month-Year (format: MM-YYYY)
- Date (format: DD MMM YYYY)
- Category (color-coded tag)
- Details (expense description)
- Amount (formatted with THB currency symbol)
- Memo notes
- Receipt (Download button if available)
- Created By (admin nickname)
- Actions (Edit and Delete buttons)

<strong>Filter Options:</strong>
1. <strong>Branch Filter:</strong> Select specific branch or view all branches
2. <strong>Current Month Button:</strong> Quick filter to current month
3. <strong>Month-Year Dropdown:</strong> Select specific month and year
   - Sorted newest to oldest
   - Format: MM-YYYY
4. <strong>Category Dropdown:</strong> Filter by expense category
   - Alphabetically sorted
   - Shows category color tags
5. Click "Refresh" to reload data
6. All filters work together for precise results

<strong>Downloading Receipts:</strong>
1. Click "Download" button in Receipt column
2. Opens receipt file in new browser tab
3. If no receipt: Shows "No Receipt" in gray text
4. <strong>Supported formats:</strong> PDF, JPEG, PNG
5. Files stored securely on server

<strong>Editing Expenses:</strong>
1. Click Edit button (blue pencil icon)
2. Modal opens with expense details pre-filled:
   - Date (calendar picker)
   - Category (dropdown with color tags)
   - Details (expense description)
   - Amount (formatted with ฿ symbol)
   - Memo (optional notes)
3. Modify any fields as needed
4. Click "OK" to save changes
5. Click "X" or "Cancel" to discard changes
6. Changes reflect immediately in table
7. Month-year automatically updated based on date

<strong>Deleting Expenses:</strong>
1. Click Delete button (red trash icon)
2. Confirmation dialog appears
3. Dialog shows: "Are you sure you want to delete this expense?"
4. Click "Yes" to confirm deletion
5. Click "No" to cancel
6. <strong>Warning:</strong> This action cannot be undone
7. Expense removed from table immediately
8. Summaries update automatically

<strong>Exporting Data:</strong>
- Click "Export CSV" button to download expenses
- Filename format: expenses_[month-year].csv or expenses_all.csv
- CSV includes:
  - Branch name
  - Month-Year, Date
  - Category name
  - Details (expense description)
  - Amount (formatted)
  - Memo notes
  - Created by (admin nickname)
- Only filtered results exported

<strong>Amount Formatting:</strong>
- Displays as: ฿ 1,234.56
- Thousand separators for readability
- Two decimal places precision
- Edit modal supports same formatting

<strong>Table Features:</strong>
- Shows 15 expenses per page (adjustable)
- Page size options: 15, 30, 50, 100
- Shows total count and current range
- Sortable columns
- Horizontal scroll for all columns
- Bordered layout for clarity

<strong>Month-Year Filter Behavior:</strong>
- Dropdown shows all available months
- Sorted from newest to oldest
- Format: MM-YYYY (e.g., 01-2025)
- "Current Month" button sets to current month automatically
- Clear selection to view all months

<strong>Important Notes:</strong>
- Only superadmin can access this page
- All amounts in Thai Baht (THB)
- Receipts optional but recommended for record-keeping
- Edit updates only expense details, not receipt file
- Categories cannot be changed during edit if not in list
- Date changes automatically update month-year
- Branch summaries show all branches even with no expenses
- Overall summary includes all filtered results
- Delete action requires confirmation
- Receipt download opens in new tab
- Memo field shows full text in table (ellipsis if too long)

<strong>Best Practices:</strong>
- Use month-year filter for monthly reports
- Export CSV for accounting reconciliation
- Review branch summaries for budget tracking
- Check "Most Common Category" to identify spending patterns
- Filter by category for detailed category analysis
- Verify amounts before deleting expenses
- Use "Current Month" for quick monthly overview
- Download receipts before deleting expenses
- Add descriptive details for future reference
- Review latest expense date to ensure recent entries
- Use branch filter to track individual branch spending
- Export filtered data for specific reports`,
  image: "/manual-images/expense-report.png",
},
        ],
      },
      {
        section: "Teacher Hours",
        topics: [
          {
  id: 42,
  title: "Teacher's Rate",
  description: `The Teacher's Rate page allows superadmin to set and manage hourly rates for teachers based on subject groups and class sizes.

<strong>Viewing Teacher Rates:</strong>
The table displays:
- Teacher ID
- Teacher Name (clickable link to teacher details)
  - Format: (Nickname) Firstname Lastname
- Subject Group Rates:
  - Organized by subject group
  - Shows rates for each class size category
  - Color-coded tags by class size
- Action column:
  - "Add Rates" button if no rates set
  - "Edit Rates" button if rates exist
  - Rate count indicator (e.g., "6/9 rates set")

<strong>Search Functionality:</strong>
- Search bar accepts:
  - Teacher ID
  - First name
  - Last name
  - Nickname
- Results filter in real-time
- Case-insensitive search

<strong>Subject Groups:</strong>
Teachers can have different rates for different subject groups. Each subject group displays separately with its own rates.

<strong>Class Size Categories:</strong>
Three categories with distinct colors and icons:
- <strong>Private (1-2 students):</strong> Red tag with single user icon
- <strong>Group (3-5 students):</strong> Blue tag with team icon
- <strong>Group (6-10 students):</strong> Green tag with team icon

<strong>Adding/Editing Teacher Rates:</strong>
1. Click "Add Rates" or "Edit Rates" button for any teacher
2. Modal opens showing teacher's name in title
3. Form organized by subject groups (with dividers)
4. Each subject group contains three input fields:
   - Private (1-2 students)
   - Group (3-5 students)
   - Group (6-10 students)
5. Enter hourly rate in Thai Baht (฿) for each category
6. Leave blank if rate not applicable
7. Click "OK" to save all rates
8. Click "Cancel" or X to discard changes

<strong>Rate Display Format:</strong>
When rates are set, they appear as color-coded tags:
- Subject group name shown with book icon
- Individual rates shown below as tags
- Format: "[Class Size]: ฿[Rate]"
- Example: "Private (1-2): ฿500"
- Only categories with rates are displayed

<strong>Rate Management:</strong>
- Can set different rates for different subject groups
- Each subject-class size combination independent
- Updating rates replaces all existing rates for that teacher
- Zero or blank fields = no rate for that category
- All rates in Thai Baht (฿)

<strong>Rate Counter:</strong>
- Shows progress: "X/Y rates set"
- X = number of rates currently configured
- Y = total possible rate combinations
- Total = (Subject Groups) × (Class Size Categories)
- Helps track completion status

<strong>Modal Form Features:</strong>
- Organized by subject group sections
- Subject groups separated by dividers with book icon
- Three columns per subject group (one per class size)
- Each field labeled with class size icon and name
- Input fields accept numbers only
- ฿ symbol automatically prefixed
- Minimum value is 0
- Width: 800px for comfortable viewing

<strong>Status Indicators:</strong>
- <strong>"No rates set" tag:</strong> Gray tag with warning icon when teacher has no rates
- <strong>Subject-specific rates:</strong> Only subjects with rates are shown
- <strong>Missing rates:</strong> Categories without rates are hidden in display

<strong>Important Notes:</strong>
- Only superadmin can access this page
- Each teacher requires individual rate configuration
- Rates specific to subject group AND class size
- Teachers can have rates for some subjects and not others
- All rates must be positive numbers
- Leaving field blank removes that rate configuration
- Rate changes apply immediately after saving
- No rate means teacher cannot be assigned to that subject-size combination
- Table shows 100 teachers per page
- All rates stored in Thai Baht currency
- Modal destroys on close (doesn't retain unsaved changes)

<strong>Best Practices:</strong>
- Set rates for all subject groups teacher is qualified to teach
- Higher rates typically for smaller class sizes (private)
- Lower rates for larger group classes
- Review rate count indicator to ensure complete setup
- Use consistent rate structure across similar subjects
- Update rates regularly based on experience level
- Leave categories blank if teacher doesn't teach that combination
- Double-check amounts before saving
- Consider different rates for specialized subjects
- Review all teachers periodically to ensure rates current

<strong>Rate Structure Guidelines:</strong>
- Private (1-2): Typically highest rate
- Group (3-5): Medium rate
- Group (6-10): Typically lowest rate per student
- Subject groups may have different base rates
- Advanced subjects may command higher rates
- New teachers may start with lower rates

<strong>Form Validation:</strong>
- All fields optional (can leave blank)
- Only accepts positive numbers
- Cannot enter negative values
- Decimal values allowed for precise rates
- Must save to apply changes
- Cancel discards all modifications

<strong>Rate Display Logic:</strong>
- Only configured rates appear in table
- Empty rates not shown to keep display clean
- Subject groups without any rates hidden
- Color coding helps quickly identify class sizes
- Icons reinforce class size categories`,
  image: "/manual-images/teacher-hourrates.png",
},
          {
  id: 43,
  title: "Teacher's Hours Details",
  description: `The Teacher's Hours Details page displays all classes taught by a specific teacher with payment approval and tracking capabilities.

<strong>Teacher Information Card:</strong>
- Displays teacher's full name with nickname
- Shows rate table for all subject groups
- Grid format: Subject | 1-2 students | 3-5 students | 6-10 students
- Rates color-coded: Green (฿) for set rates, Gray dash (-) for unset
- "No rates set" message if teacher has no configured rates

<strong>Filtering Options:</strong>
1. <strong>Branch Filter:</strong> Select specific branch or view all
2. <strong>Month Selector:</strong> Choose month (January-December)
3. <strong>Year Selector:</strong> Choose year (2024-2033)
4. <strong>Search Button:</strong> Apply selected month/year filter
5. <strong>Export CSV Button:</strong> Download filtered data
6. <strong>Show Unpaid Only:</strong> Toggle to view only unpaid classes
7. <strong>Show All Classes:</strong> View both paid and unpaid classes

<strong>Selection Features:</strong>
- <strong>Select All Checkbox:</strong> In table header to select/deselect all unpaid classes
- <strong>Individual Checkboxes:</strong> Select specific classes for approval
- <strong>Selection Counter:</strong> Shows "X classes selected" with clear option
- <strong>Paid Classes:</strong> Cannot be selected (disabled checkbox)

<strong>Class Information Table:</strong>
The table displays:
- <strong>Select:</strong> Checkbox for class selection
- <strong>Branch:</strong> Color-coded tag
- <strong>Group:</strong> Subject group name (blue tag)
- <strong>Class ID:</strong> Unique class identifier
- <strong>Class Code:</strong> Course code
- <strong>Subject Name:</strong> Full subject name
- <strong>Level:</strong> Class level
- <strong>Type:</strong> Private (blue) or Group (green) with icon
- <strong>Platform:</strong> Onsite (blue), Online (green), or At Home (orange)
- <strong>Date:</strong> Class date (format: DD MMM YYYY)
- <strong>Start - End:</strong> Time range (e.g., 14:00 - 16:00)
- <strong>Hours:</strong> Duration (e.g., 2h 0m)
- <strong>Registered Students:</strong> 
  - Count with color-coded tag
  - Purple: Private (1-2)
  - Blue: Small Group (3-5)
  - Green: Large Group (6-10)
  - Red: Oversized (>10)
  - Category name shown below count
- <strong>Rate:</strong> Hourly rate (editable for unpaid classes)
- <strong>Total:</strong> Hours × Rate calculation
- <strong>Change:</strong> Class change reason tag
- <strong>Note:</strong> Admin note about class change
- <strong>Teacher CF:</strong> Teacher confirmation status
- <strong>Admin CF:</strong> Admin confirmation status
- <strong>Payment Status:</strong> Paid (green) or Pending (orange)
  - Shows "Can Re-approve" for approved but unpaid classes
- <strong>Remark:</strong> Notes field (editable for unpaid classes)


<strong>Editing Rates and Remarks:</strong>
1. <strong>Rate Column:</strong>
   - Auto-calculated based on subject group and student count
   - Editable number input for unpaid classes
   - Shows ฿ symbol with formatting
   - Gray background for paid classes (non-editable)
2. <strong>Remark Column:</strong>
   - Text input field for notes
   - Editable for unpaid classes only
   - Gray background for paid classes (non-editable)
3. Changes save when approving hours

<strong>Table Summary Row:</strong>
- Shows total hours for selected classes
- Shows total amount for selected classes
- Format: Hours (Xh Ym) and Amount (฿X,XXX.XX)
- Updates in real-time as selections change

<strong>Approval Process:</strong>

<strong>Step 1 - Select Classes:</strong>
1. Use individual checkboxes or Select All
2. Only unpaid classes can be selected
3. Previously approved but unpaid classes can be re-selected
4. Selection counter shows total selected

<strong>Step 2 - Review and Edit:</strong>
1. Verify hourly rates (edit if needed)
2. Add remarks for any classes
3. Review total hours and amount in summary row

<strong>Step 3 - Approve Selected Hours:</strong>
1. Click "Approve Selected Hours (X)" button
2. Confirmation modal opens showing:
   - Teacher name and period
   - Number of selected classes
   - Base pay calculation

<strong>Step 4 - Add Deductions/Additions:</strong>
In the confirmation modal:

<strong>Additions:</strong>
- <strong>Other Addition Pay:</strong> Enter amount and note
- Adds to gross total

<strong>Gross Total:</strong> Base Pay + Additions (shown in green box)

<strong>Deductions:</strong>
- <strong>Withholding Tax:</strong> Checkbox for 3% deduction
  - Auto-calculates 3% of gross total when checked
- <strong>Social Security Insurance:</strong> Enter amount
- <strong>Other Deduction:</strong> Enter amount and note

<strong>Net Total Calculation:</strong>
- Displays in blue box at bottom
- Shows breakdown:
  - Base Pay
  - + Additions
  - = Gross Total
  - - Withholding Tax (if checked)
  - - Social Security
  - - Other Deduction
  - = Net Total

<strong>Step 5 - Confirm:</strong>
1. Review all calculations
2. Click "Confirm" or "Re-confirm" button
3. System saves approval
4. Success message appears
5. Table refreshes with updated status

<strong>Re-approval Feature:</strong>
- Previously approved but unpaid classes can be re-approved
- Warning alert appears in modal for re-approvals
- Allows updating rates, deductions, and additions
- Useful for correcting previous approvals before payment

<strong>Status Indicators:</strong>
- <strong>Teacher CF:</strong>
  - Green "Teacher CF": Teacher confirmed
  - Orange "Pending": Not confirmed by teacher
- <strong>Admin CF:</strong>
  - Green "Admin CF": Admin approved
  - Orange "Pending": Awaiting admin approval
- <strong>Payment Status:</strong>
  - Green "Paid": Payment completed
  - Orange "Pending": Payment not yet made

<strong>Important Notes:</strong>
- Only superadmin can access this page
- Rates auto-calculate based on subject group and student count
- Manual rate edits override auto-calculated rates
- All amounts in Thai Baht (฿)
- Can only approve unpaid classes
- Paid classes cannot be edited or re-approved
- Selection limited to unpaid classes only
- Re-approval updates existing approval record
- Net total cannot be negative (shows ฿0.00 minimum)
- Changes save only when approval confirmed
- Export CSV includes all filtered classes
- Remarks save with approval
- Multiple classes can be approved at once

<strong>Best Practices:</strong>
- Review all rates before approving
- Add remarks for unusual circumstances
- Use re-approval for corrections before payment
- Check student count matches registration
- Verify platform and type are correct
- Apply withholding tax consistently per policy
- Document all deductions with notes
- Export CSV for monthly records
- Review change reasons for accuracy
- Confirm gross and net totals before approving
- Use batch selection for efficiency
- Clear selection between different approval periods

<strong>CSV Export Contents:</strong>
- Branch, Course, Class ID
- Subject Group, Level, Subject Name
- Type, Platform
- Date, Start Time, End Time, Schedule Hours
- Registered Students, Class Size Category
- Hourly Rate, Change Reason, Note
- Total Amount, Confirm Teach Status
- Admin Approval, Payout Status, Remark

<strong>Button States:</strong>
- <strong>"Approve Selected Hours (X)":</strong> Active when unpaid classes selected
- <strong>"Re-approve Selected Hours (X)":</strong> Shows for previously approved unpaid classes
- <strong>"All Classes Paid":</strong> Disabled button when no unpaid classes exist
- Disabled when: No selection, only paid classes selected, or all paid`,
  image: "/manual-images/teacher-details.png",
},
          {
  id: 44,
  title: "Teacher Class Logs",
  description: `The Teacher Class Logs page displays all classes across all teachers with comprehensive filtering and statistics tracking.

<strong>Statistics Dashboard:</strong>
Displays key metrics for selected date range:

<strong>Row 1 - Total Hours:</strong>
- <strong>Total Hours:</strong> Sum of all class hours in filtered period (blue)
- Shows active date range below the total

<strong>Row 2 - Teacher & Admin Status:</strong>
- <strong>Teacher Confirmed (Taught Classes):</strong> Hours confirmed by teachers (green)
- <strong>Teacher Unconfirmed:</strong> Hours not yet confirmed by teachers (orange)
- <strong>Admin Confirmed:</strong> Hours approved by admin (green)
- <strong>Admin Unconfirmed:</strong> Hours pending admin approval (orange)

<strong>Row 3 - Payment Status:</strong>
- <strong>Paid Hours:</strong> Hours already paid out (green)
- <strong>Unpaid Hours:</strong> Hours awaiting payment (red)

All statistics update automatically based on applied filters.

<strong>Filter Options:</strong>
1. <strong>Branch Filter:</strong> Select specific branch or view all branches
2. <strong>Date Range:</strong> 
   - From date picker (defaults to start of current month)
   - To date picker (defaults to end of current month)
   - Format: DD MMM YYYY
3. <strong>Class Status Filter:</strong>
   - All Class Statuses
   - Confirmed (teacher approved)
   - Confirm Pending
4. <strong>Admin Approve Filter:</strong>
   - All statuses
   - Admin Confirmed
   - Pending
5. <strong>Payout Filter:</strong>
   - All statuses
   - Pending (not paid)
   - Paid
6. <strong>Export CSV:</strong> Download filtered data
<img src="/manual-images/teacher-class-log2.png" alt="Teacher Class Logs" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />

<strong>Viewing Class Logs:</strong>
The table displays:
- <strong>Branch:</strong> Color-coded branch tag
- <strong>Date:</strong> Class date (format: DD Mon YY)
  - Sortable column
- <strong>Class Code:</strong> Course identifier
- <strong>Class ID:</strong> Unique class number
- <strong>Pay Group:</strong> Subject group for rate calculation
- <strong>Subject:</strong> Full subject name
- <strong>Teacher:</strong> Format: (Nickname) Full Name
- <strong>Platform:</strong> Location type with icon
  - Onsite (Blue with location icon)
  - Online (Green with camera icon)
  - At Home (Orange with home icon)
- <strong>Time:</strong> Start - End time range (e.g., 14:00 - 16:00)
- <strong>Hours:</strong> Duration (e.g., 2h 0m)
- <strong>Students:</strong> Registered student count with category
  - Purple tag: Private (1-2 students)
  - Blue tag: Small Group (3-5 students)
  - Green tag: Large Group (6-10 students)
  - Red tag: Oversized (>10 students)
  - Category label shown below count
- <strong>Rate:</strong> Hourly rate based on subject group and class size
  - Shows "No Rate Set" if teacher has no configured rate
- <strong>Total:</strong> Calculated as Hours × Rate
  - Shows "No Rate Set" if rate unavailable
- <strong>Approved Rate:</strong> Rate used when admin approved
  - Shows dash (-) if not approved
- <strong>Paid:</strong> Amount actually paid to teacher
  - Shows dash (-) if not yet paid
- <strong>Teacher CF:</strong> Teacher confirmation status
  - Green "Teacher CF": Confirmed/Taught
  - Orange "Pending": Not confirmed
- <strong>Admin CF:</strong> Admin approval status
  - Green "Admin CF": Approved
  - Orange "Pending": Not approved
- <strong>Payout:</strong> Payment status
  - Green "Paid": Payment completed
  - Red "Pending": Payment not made

<strong>Rate Calculation Logic:</strong>
- Rate automatically determined by:
  1. Teacher's subject group
  2. Number of registered students (class size)
  3. Rate table configuration
- <strong>Class Size Categories:</strong>
  - Private (1-2 students): Highest rate
  - Small Group (3-5 students): Medium rate
  - Large Group (6-10 students): Lower rate
  - Oversized (>10 students): Uses private rate
- Shows "No Rate Set" if teacher lacks configuration

<strong>Date Range Functionality:</strong>
- Defaults to current month on page load
- Both dates must be selected for filtering
- "To" date cannot be before "From" date
- Statistics recalculate when date range changes
- Date range shown under Total Hours statistic

<strong>CSV Export Contents:</strong>
- <strong>Filename:</strong> Dynamic based on date range
  - Format: tutor_class_log_YYYY-MM-DD_to_YYYY-MM-DD.csv
- <strong>Columns included:</strong>
  - Branch Name, Class ID, Date
  - Class Code, Subject, Subject Group
  - Teacher (with nickname)
  - Platform, Time range, Schedule Hour
  - Registered Students, Class Size Category
  - Rate, Total amount
  - Tutor Confirm, Admin Confirm, Payout status

<strong>Table Features:</strong>
- Shows 100 classes per page (default)
- Page size options: 100, 200, 500
- Horizontal scroll for all columns
- Bordered layout for clarity
- Medium size cells
- Pagination controls at bottom

<strong>Status Color Coding:</strong>
- <strong>Teacher Confirmation:</strong>
  - Light green background: Approved
  - Light yellow background: Pending
- <strong>Admin Confirmation:</strong>
  - Light green background: Confirmed
  - Light yellow background: Pending
- <strong>Payout Status:</strong>
  - Light green background: Paid
  - Light red background: Pending

<strong>Important Notes:</strong>
- Only superadmin can access this page
- Date range required for data display
- Rates specific to each teacher's configuration
- No rate = cannot calculate total
- Statistics reflect only filtered classes
- All amounts in Thai Baht (฿)
- Time calculations include minutes
- Student count comes from registration system
- Multiple filters work together
- Export includes only filtered results
- Date range defaults to current month
- Branch filter shows all branches in system

<strong>Best Practices:</strong>
- Set date range first before other filters
- Use branch filter for location-specific reports
- Check "No Rate Set" classes regularly
- Review unpaid hours for payment processing
- Export CSV for monthly accounting
- Monitor teacher confirmation rates
- Track admin approval progress
- Use payout filter to identify pending payments
- Review statistics for workload analysis
- Check oversized classes for policy compliance
- Verify rates match configuration
- Use date range for period reports


<strong>Statistics Usage:</strong>
- <strong>Total Hours:</strong> Overall teaching volume
- <strong>Teacher Confirmed:</strong> Classes actually taught
- <strong>Teacher Unconfirmed:</strong> Follow up with teachers
- <strong>Admin Confirmed:</strong> Ready for payment processing
- <strong>Admin Unconfirmed:</strong> Requires admin review
- <strong>Paid Hours:</strong> Completed payment cycle
- <strong>Unpaid Hours:</strong> Pending payment tasks

<strong>Filter Combinations:</strong>
- Date + Branch: Location-specific period report
- Date + Teacher Confirmed: Actual taught classes
- Date + Unpaid: Classes awaiting payment
- Admin Confirmed + Unpaid: Ready for payment processing
- Teacher Confirmed + Admin Pending: Requires admin action`,
  image: "/manual-images/teacher-class-logs1.png",
},
         {
  id: 45,
  title: "Payment Report",
  description: `The Payment Report page displays all approved teacher payment batches with document generation capabilities.

<strong>Viewing Payment Reports:</strong>
The table displays:
- <strong>Approval Date:</strong> Date when payment was approved
  - Format: DD-MM-YYYY
  - Sortable column
- <strong>Period:</strong> Payment period
  - Format: MMM YY (e.g., Jan 25)
  - Represents the month being paid
- <strong>Teacher Name:</strong> Full name of teacher
- <strong>National ID:</strong> Formatted as X XXXX XXXXX XX X
- <strong>Hours:</strong> Total hours in the payment batch
  - Sortable column
- <strong>Sub Amount:</strong> Base pay before additions (฿)
- <strong>Addition Pay:</strong> Other additions amount (฿)
- <strong>Gross Total:</strong> Sub Amount + Addition Pay (฿)
- <strong>Withholding Tax:</strong> Tax deducted (3% if applied) (฿)
- <strong>Deduction:</strong> Other deductions amount (฿)
- <strong>Net Total:</strong> Final payment amount (฿)
- <strong>Approve By:</strong> Admin who approved the batch
- <strong>Doc:</strong> Paperclip icon to view pay stub
- <strong>W/H:</strong> PDF icon to download withholding tax form (Tawi 50)
- <strong>Actions:</strong> Delete button (red)

<strong>Filter Section:</strong>
Located at top of page with gray background:

1. <strong>Teacher Filter:</strong>
   - Dropdown with all teacher names
   - Searchable - type to find teacher
   - Shows "Select Teacher" placeholder
   - Clear option available

2. <strong>Period Filter:</strong>
   - Dropdown with all payment periods
   - Format: MMM YY
   - Sorted newest first
   - Shows "Select Period" placeholder
   - Clear option available

3. <strong>Reset Button:</strong>
   - Clears both filters
   - Disabled when no filters applied
   - Returns to showing all reports

4. <strong>Filter Button:</strong>
   - Blue button to apply selected filters
   - Works with one or both filters
<img src="/manual-images/teacher-payment2.png" alt="Teacher Paystub" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Viewing Pay Stub:</strong>
1. Click paperclip icon in Doc column
2. Modal opens showing complete pay stub
3. Pay stub includes:
   - Company information (KEAES Academics)
   - Company address and contact details
   - Teacher information
   - Pay period and payment date
   - Total hours and number of classes
   - <strong>Earnings breakdown:</strong>
     * Sub Amount (base pay)
     * Other Addition Pay (if any)
     * Addition note
     * Gross Total
   - <strong>Deductions breakdown:</strong>
     * Withholding Tax (3% if applied)
     * Social Security Deduction
     * Other Deduction
     * Deduction note
   - Net Total (take-home pay)
4. Can print or save as PDF from modal
5. Click X or outside modal to close

<strong>Generating Withholding Tax Form (Tawi 50):</strong>
1. Click PDF icon in W/H column
2. System automatically generates Tawi 50 form
3. Pre-filled information:
   - <strong>Section 1 (Payer):</strong>
     * Name: KEAES Academics
     * Address: 982/22 Gateway Ekkamai Fl.4...
     * Tax ID: 0 0000 00000 00 0
   - <strong>Section 2 (Payee):</strong>
     * Teacher name
     * Teacher address
     * Teacher national ID (formatted)
   - <strong>Tax Details:</strong>
     * Date of payment
     * Gross amount paid
     * Tax withheld amount (฿ and in words)
     * Checkboxes marked appropriately
4. PDF downloads automatically
5. Filename: Tawi50_[TeacherName]_[Period].pdf
6. Opens in default PDF viewer

<strong>Important Notes:</strong>
- Only superadmin can access this page
- Each row represents one payment approval batch
- Payment batch created when admin approves teacher hours
- One teacher can have multiple batches per period (if re-approved)
- All amounts calculated during approval process
- Withholding tax form only includes tax amount (not other deductions)
- Pay stub shows complete payment breakdown
- Delete removes entire batch, not individual classes
- National ID must be 13 digits to format correctly
- Period stored as YYYY-MM in database`,
  image: "/manual-images/teacher-payment1.png",
}
        ],
      },
      {
        section: "Users",
        topics: [
          {
  id: 46,
  title: "Users Control Page",
  description: `The User Control page allows superadmin to manage all system users, their roles, and access status.

<strong>User Statistics Dashboard:<strong>
Four role-based boxes display for each role (Superadmin, Admin, Teacher, Student):
- <strong>Max User<strong>: Total allowed active users for this role
- <strong>Active User<strong>: Currently active users
- <strong>Inactive User<strong>: Currently inactive users
- <strong>Remaining Users<strong>: Available slots (color-coded):
  - Green: More than 20% slots available
  - Orange: Less than 20% slots available
  - Red: No slots available

<strong>Maximum User Limits:<strong>
- Superadmin: 10 active users
- Admin: 30 active users
- Teacher: 120 active users
- Student: 1000 active users

<strong>Student Branch Statistics:<strong>
Separate boxes show student counts per branch:
- Active Students per branch
- Inactive Students per branch
- Total Students per branch (in tag at bottom)
- Each branch displayed in different color (Purple, Cyan, Orange-red)

<strong>Viewing Users:<strong>
The table displays:
- User ID
- Branch (color-coded tag)
- Full Name (hover to see address and phone)
- Nickname
- Email
- Role (color-coded):
  - Superadmin: Dark Blue
  - Admin: Orange
  - Teacher: Dark Green
  - Student: Dark Red
- Status (Active/Inactive dropdown)
- Last Sign-in (date and days ago)
- Info icon (hover for address and phone details)
- Edit button (for admin/superadmin only)
- Reset Password button

<strong>Search and Filter:<strong>
1. <strong>Search by Name<strong>: Enter name to find users
2. <strong>Status Filter<strong>: All Status/Active/Inactive
3. <strong>Branch Filter<strong>: Filter by specific branch or All Branches
4. <strong>Role Filter<strong>: Filter by specific role or All Roles
5. All filters work together
6. Results update in real-time

<strong>Creating Admin Users:<strong>
1. Click "Create Admin" button (top right)
2. Modal form opens with fields:
   - First Name (required)
   - Last Name (required)
   - Nickname (required)
   - Email (required)
   - Password (required)
   - Role: Select Admin or Superadmin
   - Branch: <strong>Required for Admin role<strong> (select from dropdown)
3. Click "Create" to save
4. New admin appears in table immediately

<strong>Important: Admin users MUST have a branch assigned<strong>

<strong>Editing Admin Users:<strong>
1. Click Edit button (pencil icon) on any admin/superadmin row
2. Modal opens with pre-filled information
3. Modify any fields (leave password blank to keep existing)
4. <strong>Change branch if needed (required for admin role)<strong>
5. Click "Save" to update
6. Changes reflect immediately in table

<strong>Note:<strong> User ID 1 (primary superadmin) cannot be edited or have password reset

<strong>Changing User Status:<strong>
1. Click status dropdown for any user
2. Select Active or Inactive
3. Confirmation dialog appears
4. System checks if role limit reached
5. If limit reached, warning appears
6. Status updates immediately after confirmation

<strong>Resetting User Password:<strong>
1. Click "Reset Password" button for any user (except User ID 1)
2. Confirmation dialog appears
3. System generates temporary password
4. Success notification displays with temporary password
5. <strong>Important:<strong> Copy temporary password immediately
6. Provide temporary password to user
7. User must change password on next login

<strong>Role-Based Limitations:<strong>
- Cannot activate user if role limit reached
- Warning appears when trying to exceed limits
- Remaining slots shown in dashboard
- Each role has independent limits

<strong>Important Notes:<strong>
- Only superadmin can access this page
- User ID 1 is protected from edits and password reset
- Branch selection required when creating/editing Admin users
- Superadmin users do not require branch assignment
- Password field in edit mode is optional (leave blank to keep current)
- Status changes require confirmation
- Password reset generates random temporary password
- Last sign-in shows "Never Sign In" if user hasn't logged in
- Table displays 50 users per page
- Hover over name for address and phone details
- Info icon shows additional user details

<strong>Best Practices:<strong>
- Monitor remaining user slots regularly
- Deactivate unused accounts to free up slots
- Assign appropriate branches to admin users
- Store temporary passwords securely when resetting
- Review last sign-in dates to identify inactive accounts
- Use status filters to manage active/inactive users
- Keep contact information updated in user profiles
- Verify branch assignment for all admin users
- Use role filters to review users by access level`,
  image: "/manual-images/user-management.png",
},
        ],
      },
    ],
  },
  {
    category: "Teacher",
    sections: [
      {
        section: "Dashboard",
        topics: [
          {
  id: 47,
  title: "Dashboard",
  description: `The Teacher Dashboard provides an overview of your teaching schedule, hours, and class assignments.

<strong>Teacher Profile Header:</strong>
- Displays your name, photo, email, and online status
- Shows date joined and last sign-in time

<strong>Today's Classes Card:</strong>
- Lists all classes scheduled for today
- Shows class code (clickable to view details), subject name, room number, and branch
- Displays time range, platform (Onsite/Online/At Home), and class type (Private/Group)
- Automatic notifications: "Classes today" alert on page load, 10-minute warning before class starts
- Empty state shown if no classes today

<strong>Statistics Cards:</strong>
- <strong>Current Month Hours:</strong> Total teaching hours this month (blue gradient card)
- <strong>Average Hours/Week:</strong> Monthly hours ÷ 4 (gold gradient)
- <strong>Average Hours/Day:</strong> Monthly hours ÷ days in month (purple gradient)

<strong>Monthly Hours Trend Chart:</strong>
- Bar chart showing teaching hours over recent months
- Visual trend analysis of workload

<strong>Upcoming Classes Table:</strong>
- Shows future classes sorted by start date
- Columns: Class Code (clickable), Branch (color-coded), Subjects, Start/End Date, Total Hours, My Hours, Type, Platform
- Search bar to filter by course name
- Pagination: 10 per page (adjustable)

<strong>All Classes Table:</strong>
- Shows all past classes sorted newest first
- Same columns as Upcoming Classes
- Search bar to filter by course name
- Complete class history for reference

<strong>Key Features:</strong>
- Real-time notifications for today's classes
- Click class code to view full class details
- Color-coded branches: Branch 1 (Blue), Branch 2 (Green), Branch 3 (Magenta)
- Responsive design for mobile/desktop viewing`,
  image: "/manual-images/teacher-dashboard.png",
}
        ],
      },
      {
        section: "Class Management",
        topics: [
            {
  id: 15,
  title: "Course Details",
  description: `The Course Details page shows complete information about a specific course, divided into individual class slots.

<strong>Access:</strong> Only teachers and students assigned to this course can view it.

<strong>Course Header:</strong>
- Course code and name
- Subject list
- Assigned teachers

<strong>Course Information Card:</strong>
- Start date and total hours
- Notes/materials required
- Platform (Onsite/Online/At Home)
- Type (Private/Group)
- Schedule Details button

<strong>Class Performance (Pie Chart):</strong>
- Present, Absent, Late percentages
- Visual attendance statistics

<strong>Registered Students List:</strong>
- Shows all enrolled students with nicknames
- Total student count displayed

<strong>Messages Section:</strong>
- Communication between teachers, students, and admins
- Role tags (Student/Teacher/Admin/Superadmin)
- Send new messages with text area

<strong>Individual Class Slots:</strong>
Each class displays:
- Class number and ID (clickable to view lesson plan)
- Subject name and teacher nickname
- Video meeting link section (add/join/delete)
- Date, time, duration, and room number
- Class comment area (teacher can add notes)
- Homework upload section (PDF files)
- Three action buttons: Homework (view submissions), Attendance (mark attendance), Feedback (submit student feedback)

<strong>Key Features:</strong>
- Click Class ID to view lesson plan
- Add video meeting links for online classes
- Upload homework PDFs for students
- View submitted homework with download report
- Mark attendance with status and comments
- Submit individual student feedback
- Export attendance and homework reports as CSV`,
  image: "/manual-images/class-details1.png",
},
{
  id: 48,
  title: "Add/Check Homework",
  description: `Upload homework assignments and review student submissions for each class slot.

<strong>Upload Homework (Teacher):</strong>
- Click "Choose PDF" to select homework file
- Click "Upload" button to post assignment
- Uploaded files appear under "Homework" section
- Students can download files from course page

<strong>View Submissions:</strong>
- Click "Homework" button on class card
- Opens drawer showing all submitted homework
- Displays: Student name, file name, upload date/time
- Download individual submissions or export CSV report

<strong>Submission Summary:</strong>
- Shows total number of submissions
- Lists submitted homework with green check marks
- "No Homework Submitted" message if none received

<strong>Quick Actions:</strong>
- Export Report: Download CSV with all submission details
- Download: Individual file downloads
- Empty state shown when no submissions`,
  image: "/manual-images/class-details2.png",
},
{
  id: 49,
  title: "Add Student Attendance",
  description: `Mark and track student attendance for each class session.

<strong>Open Attendance:</strong>
- Click "Attendance" button on class card
- Opens drawer with student list
- Button shows lock icon if already submitted

<strong>Mark Attendance:</strong>
- Select status for each student: Present, Absent, Late (≤15M)
- Select homework status: YES, NO, N/A
- Add optional comments
- Click "Save" to record attendance

<strong>Attendance Summary:</strong>
- Green tag: Present count
- Orange tag: Late count  
- Red tag: Absent count

<strong>Edit Attendance:</strong>
- Click "Edit" button on saved record
- Modify status, homework, or comments
- Click "Update" to save changes
- Shows timestamp and "Edited" indicator

<strong>Quick Actions:</strong>
- Export Report: Download CSV with attendance data
- Mark All Present: Bulk action for quick marking

<strong>Locked Attendance:</strong>
- Cannot edit if class is locked (future dates or different teacher)
- Once submitted, attendance is locked but can be edited`,
  image: "/manual-images/class-details3.png",
},
{
  id: 50,
  title: "Add Student Feedback",
  description: `Submit detailed feedback for individual students after each class.

<strong>Open Feedback:</strong>
- Click "Feedback" button on class card
- Opens feedback form drawer

<strong>Feedback Form Fields:</strong>
- <strong>Student:</strong> Select from dropdown (required)
- <strong>Attendance & Punctuality:</strong> Text area
- <strong>Class Participation:</strong> Text area
- <strong>Comprehension:</strong> Text area
- <strong>Focus & Behavior:</strong> Text area
- <strong>Progress Since Last Class:</strong> Text area
- <strong>Areas to Improve:</strong> Text area
- <strong>Teacher's Comment:</strong> Text area

<strong>Feedback Status:</strong>
- Orange "Pending Approval" tag when submitted
- Green "Approved" tag once approved by admin
- Cannot edit after approval

<strong>Loading Previous Feedback:</strong>
- Select student to load existing feedback
- Existing feedback auto-populates form fields
- Can update pending feedback before approval

<strong>Submit Feedback:</strong>
- Click "Submit" button to save
- Creates new feedback or updates existing
- Success message confirms submission
- Form closes automatically after save`,
  image: "/manual-images/class-details4.png",
}
        ],
      },
      {
        section: "Calendar",
        topics: [
          {
  id: 51,
  title: "Calendar",
  description: `Manage your availability and notify school admin of days off or unavailable time.

<strong>Key Feature:</strong>
- All calendar entries are automatically visible to school admin without manual sharing
- Used to help admin schedule classes around your availability

<strong>Adding Day Off / Unavailable Time:</strong>
1. <strong>Drag on Calendar:</strong> Click and drag to select time range
2. <strong>Add Details:</strong>
   - Title: Write "DAY OFF", "UNAVAILABLE", or "HOLIDAY"
   - Description: Optional reason/notes
3. <strong>Set Recurrence (Optional):</strong> For regular days off (e.g., every Sunday)
4. Event automatically visible to admin - no sharing needed
<img src="/manual-images/teacher-calendar2.png" alt="Change Reason Modal" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Editing Events:</strong>
- <strong>Drag & Drop:</strong> Move event to new time slot
- <strong>Resize:</strong> Drag top/bottom border to adjust start/end time
- Changes update automatically

<strong>Deleting Events:</strong>
- Click on event block
- Choose delete option:
  - Just this occurrence
  - All recurring events (if applicable)

<strong>Optional Sharing:</strong>
You can additionally share with:
- Specific roles (e.g., all tutors)
- Individual users (e.g., specific teacher/coordinator)

<strong>Best Practices:</strong>
- Use clear titles (DAY OFF, UNAVAILABLE, HOLIDAY)
- Set recurring events for regular days off
- Update calendar as soon as you know unavailability
- Admin sees all entries automatically for scheduling`,
  image: "/manual-images/teacher-calendar1.png",
}
        ],
      },
      {
        section: "My Hours",
        topics: [
          {
  id: 52,
  title: "Confirm Class",
  description: `Confirm classes you've taught to approve your teaching hours for payment processing.

<strong>Three Tabs Available:</strong>
- Classes: View and confirm individual classes
- Hours Summary: Monthly hours overview
- Payout: Payment history and details

<strong>Filter Options (Classes Tab):</strong>
- Month selector (January-December)
- Year selector (2024-2033)
- Payment status: All Payouts, Pending, Paid

<strong>Classes Table Columns:</strong>
- Branch (color-coded tag)
- Subject Group (blue tag)
- Class ID, Class Code, Subject
- Date, Time range, Hours
- Type (Private/Group tag)
- Platform (Onsite/Online/At Home icon tags)
- Approve button/status
- Payment status (Pending orange / Paid green)
- Class Change reason tag
- Note from admin

<strong>Approving Classes:</strong>
- <strong>Button hidden:</strong> Before class ends
- <strong>"Confirm Teach" button:</strong> Available after class ends
- <strong>"Late Approval" button:</strong> Shows after 3 days (grayed out style)
- Click confirm to approve - requires confirmation dialog
- Once approved: Shows green "HOURS APPROVED" tag
- Cannot approve before class session ends

<strong>Approval Rules:</strong>
- Only after class end time
- Warning notification if more than 5 days late
- Late approvals still processable but flagged
- Confirmed classes locked for payment

<strong>Table Summary Row:</strong>
- Shows total hours for selected month/year
- Format: "Total Hours for [Month Year]: Xh Ym"
- Updates based on filtered classes

<strong>Payment Status:</strong>
- Orange "Pending": Awaiting payment
- Green "Paid": Payment completed
- Filter to view unpaid classes

<strong>Best Practices:</strong>
- Confirm classes promptly after teaching
- Avoid late confirmations (after 3 days)
- Check class changes and notes
- Review monthly totals regularly
- Confirm before payment deadline`,
  image: "/manual-images/teacher-confirm-class.png",
},
          {
  id: 53,
  title: "Check Hours Summary and Payout",
  description: `View your teaching hours summary and payment history with detailed pay stubs.

<strong>Statistics Cards (Top Row):</strong>
- <strong>Total Earnings:</strong> All-time total paid (green, ฿)
- <strong>Total Hours:</strong> All confirmed teaching hours (blue)
- <strong>Latest Payment:</strong> Most recent payment date (purple)
- <strong>Total Payments:</strong> Number of payment batches received (red)

<strong>Payment History Table Columns:</strong>
- Payment Date: When payment was approved
- Pay Period: Date range covered (e.g., "1 Jan 2025 - 31 Jan 2025")
- Total Hours: Teaching hours in period
- Sub Amount: Base pay before additions (฿)
- Additions: Extra payments added (฿)
- Deductions: Total deductions (W/H Tax + Social Security + Other)
- W/H Tax: Withholding tax amount (฿)
- Net Amount: Final payment received (฿)
- Approved By: Admin who processed payment
- Details: "View" button to see pay stub

<strong>Viewing Pay Stub:</strong>
Click "View" button to open detailed pay stub modal showing:

<strong>Company Section:</strong>
- Company logo, name, address
- Tax ID and contact information

<strong>Teacher Section:</strong>
- Your name, address, phone
- National ID and email

<strong>Payment Summary:</strong>
- Pay period and payment date
- Total hours and number of classes

<strong>Earnings Breakdown:</strong>
- Sub Amount (base hourly pay)
- Other Addition (+ extra pay with note)
- Gross Total

<strong>Deductions Breakdown:</strong>
- Withholding Tax (3% if applied)
- Social Security Deduction
- Other Deduction (with note)

<strong>Classes Breakdown Table:</strong>
- Class ID, Subject, Level, Subject Group
- Class Date, Hours worked
- Hourly Rate, Total amount
- Remark from admin

<strong>Net Payment:</strong>
- Final take-home amount after all deductions

<strong>Table Features:</strong>
- 50 payments per page
- Sortable by Payment Date, Total Hours, Net Amount
- Horizontal scroll for all columns
- Empty state if no payments

<strong>Amount Formatting:</strong>
- All amounts with thousand separators
- Two decimal places (฿ 12,345.67)
- Thai Baht currency (฿)

<strong>Best Practices:</strong>
- Review pay stubs for accuracy
- Check deductions match expectations
- Verify hours against confirmed classes
- Keep pay stub records for taxes
- Contact admin if discrepancies found`,
  image: "/manual-images/teacher-hours-payout.png",
}
        ],
      },
      {
        section: "Lesson Plan",
        topics: [
          {
  id: 54,
  title: "Lesson Plan",
  description: `Create and manage detailed lesson plans for your scheduled classes.

<strong>Three Tabs Available:</strong>
- <strong>Upcoming:</strong> Future lesson plans
- <strong>Past:</strong> Completed/cancelled plans
- <strong>All:</strong> Complete list of all plans

<strong>Filter Options:</strong>
- Search: Find by title or description
- Filter By Class: Select specific class
- Refresh: Update data

<strong>Creating Lesson Plan:</strong>
1. Click "+ Create" button
2. Select class from dropdown (only shows future classes without existing plans)
3. Form auto-fills: Title, Date, Start/End Time (based on selected class)
4. Fill required sections:
   - <strong>Lesson Brief:</strong> Overview description
   - <strong>Learning Objective:</strong> What students will learn
   - <strong>Activities:</strong> Classroom activities planned
   - <strong>Assessment:</strong> How learning will be assessed
   - <strong>Homework:</strong> Assignments for students
   - <strong>Materials:</strong> Required materials list
5. Click "Save"

<strong>Important Notes:</strong>
- Cannot create lesson plan for past classes
- One lesson plan per class date
- Title, Date, Time auto-filled from class schedule
- Class selection disabled when editing

<strong>Table Columns (Desktop):</strong>
- Class Code, Class ID (blue tag)
- Class (lesson title)
- Date (DD MMM YY format)
- Time (HH:MM - HH:MM)
- Status: Planned (blue), Completed (green), Cancelled (red)
- Actions: Edit, View, Mark Complete, Mark Cancelled, Delete

<strong>Mobile View:</strong>
Compact display showing:
- Lesson title, class code tag, date
- Status tag
- Action buttons (Edit, View, Delete)

<strong>Actions Available:</strong>
- <strong>Edit:</strong> Modify lesson plan details
- <strong>View:</strong> See complete plan (opens modal)
- <strong>Mark Completed:</strong> Green checkmark (only for planned status)
- <strong>Mark Cancelled:</strong> Red X (only for planned status)
- <strong>Delete:</strong> Remove plan (requires confirmation)

<strong>View Modal Features:</strong>
- Complete lesson plan details
- Class information
- Teacher information
- Download as PDF option
- Print option
<img src="/manual-images/teacher-lesson-plan2.png" alt="Lesson Plan Modal" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Status Updates:</strong>
- Planned → Can mark as Completed or Cancelled
- Completed/Cancelled → No further status changes
- Actions buttons hide after status change

<strong>Best Practices:</strong>
- Create plans before class date
- Include detailed learning objectives
- List all required materials
- Describe assessment methods clearly
- Mark as completed after teaching
- Use cancellation for class changes
- Download/print for offline reference`,
  image: "/manual-images/teacher-lesson-plan.png",
}
        ],
      },
    ],
  },
  {
    category: "Student",
    sections: [
      {
        section: "Dashboard",
        topics: [
          {
  id: 55,
  title: "Dashboard",
  description: `Overview of student information, class schedule, attendance, and performance tracking.

<strong>Student Header Card:</strong>
- Profile photo, name, school ID, education level
- Email and phone contact
- Four statistics: Total Classes, Attendance %, Upcoming classes, Homework %
<img src="/manual-images/student-dashboard2.png" alt="Change Reason Modal" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Calendar Section:</strong>
- Month/Week view toggle
- Color-coded events: Green (upcoming), Gray (past)
- Shows class code, time range, subject, teacher, room
- "Download Calendar PDF" button generates formatted PDF with logo and student info
<img src="/manual-images/student-dashboard3.png" alt="Change Reason Modal" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Class Schedule Table:</strong>
Two views with tabs:
- <strong>Upcoming:</strong> Future classes sorted by date
- <strong>Past:</strong> Previous classes reverse chronological

<strong>Table Columns:</strong>
- Date (with day of week), Class ID
- Class (code + subject)
- Teacher name
- Time (start-end with duration)
- Platform: Onsite (blue), Online (green), At Home (orange)
- Class Change: Color-coded tags (Admin, CL by S./T., On hold, CSD, Teacher Late, Make Up, etc.)
- Note: Admin notes about changes
- Attendance: Present (green), Absent (red), Late (orange)
- Homework: YES (green), NO (red), N/A (gray)


<strong>Notifications:</strong>
- <strong>Absence alerts:</strong> Shows recent absences with count (e.g., "2nd absence")
- <strong>Test reminders:</strong> 1 day before scheduled test

<strong>Best Practices:</strong>
- Check Upcoming tab regularly for classes
- Download calendar PDF for offline reference
- Review attendance and homework status
- Monitor absence count per class
- Note class changes and reasons`,
  image: "/manual-images/student-dashboard1.png",
}
        ],
      },
      {
        section: "Class Management",
        topics: [
          {
  id: 56,
  title: "Class Management",
  description: `View class details, access materials, submit homework, and provide feedback for enrolled courses.

<strong>Class Header Card:</strong>
- Course code (large display)
- Subjects list (comma-separated)
- Teachers assigned (comma-separated)

<strong>Class Information Section:</strong>
Five key details:
- Start Date, Total Hours
- Notes/Materials required
- Platform (Onsite/Online/At Home)
- Type (Private/Group)
- "Schedule Details" button (opens full timetable modal)

<strong>Class Performance (Pie Chart):</strong>
- Present %, Absent %, Late %
- Visual pie chart with color coding
- Based on your attendance records

<strong>Registered Students List:</strong>
- Shows all enrolled classmates
- Avatar with first letter of name
- Full name with nickname
- Total count displayed

<strong>Messages Section:</strong>
- Communication with teachers/admins
- Role tags: Student (blue), Teacher (green), Admin (red), Superadmin (gray)
- Send new messages via text area
- Shows date and time of each message

<strong>Individual Class Cards:</strong>
Each class session displays:
- Class number, Class ID, Subject
- Teacher name with icon
- Video meeting link (clickable "Join" if available)
- Date, Time, Duration, Room number
- Teacher's class comment (read-only)

<strong>Homework Section:</strong>
Three parts per class:
1. <strong>Homework from Teacher:</strong>
   - PDF files uploaded by teacher
   - Click filename to download
   - Shows if no homework uploaded
2. <strong>Upload Your Homework:</strong>
   - Click to choose PDF file
   - "Upload" button (enabled when file selected)
   - One file per class
3. <strong>Your Uploaded Homework:</strong>
   - Shows your submitted files
   - Filename with upload date/time
   - Download icon to re-download

<strong>Rating & Feedback (Today's Classes Only):</strong>
- 5-star rating system (click stars)
- Text area for comments
- "Submit Rating" button
- Available only on class day
- After submission: Shows "Thank you" message with your rating/comment

<strong>Schedule Details Modal:</strong>
Opens full timetable showing:
- Day of week, Date
- Subject Name
- Time (Start - End)
- Teacher Name, Hours
- Sorted by date and time
- Scrollable table for all sessions

<strong>Best Practices:</strong>
- Check video meeting links before class time
- Download homework PDFs when posted
- Upload homework before deadline
- Provide rating and feedback on class day
- Review teacher comments for each class
- Check messages regularly for updates
- Keep track of all class schedules`,
  image: "/manual-images/class-details-student.png",
}
        ],
      },
      {
        section: "Me",
        topics: [
          {
  id: 57,
  title: "Me",
  description: `Track your academic performance, course progress, and attendance across all enrolled courses.

<strong>Three Main Tabs:</strong>
- Overview, Courses, Timeline

<strong>Overview Tab:</strong>

<strong>Top Summary Cards (4 gradient cards):</strong>
- <strong>Active Courses:</strong> X / Total courses (purple gradient)
- <strong>Total Study Hours:</strong> Completed hours across all courses (pink gradient)
- <strong>Hours Remaining:</strong> Upcoming class hours (blue gradient)
- <strong>Study Progress:</strong> Overall completion percentage (green gradient)

<strong>Course Progress Details:</strong>
Grid of cards showing each enrolled course:
- Course code with Active/Completed tag
- Progress bar: Study Hours / Total Hours with percentage
- Four stat boxes: Present count (green), Absent count (red), Upcoming count (blue), Attendance % (purple)
- Quick summary: "X of Y classes completed"

<strong>Study Insights Card:</strong>
Three key metrics:
- <strong>Study Efficiency:</strong> Percentage of classes attended successfully
- <strong>Total Scheduled:</strong> All scheduled hours across courses
- <strong>Active Courses:</strong> Number of courses currently in progress

<strong>Courses Tab:</strong>

For each enrolled course shows:
- <strong>Course Header:</strong> Avatar, course code, subject name, attendance rate %
- <strong>Hours Summary Section:</strong>
  - Total Hours, Completed Hours (green), Upcoming Hours (purple)
  - Progress bar showing completion percentage
- <strong>Attendance Statistics:</strong> Three cards for Present (green), Late (yellow), Absent (red)
- <strong>Class Cards Grid:</strong> Individual class sessions showing:
  - Subject name with status tag (Upcoming/Completed/Late/Absent)
  - Border color matches status (green/yellow/red/gray)
  - Date, time range, duration tag
  - Teacher name, room number
  - Homework status tag (YES green / NO red)
  - Teacher comments in alert box (if any)

<strong>Timeline Tab:</strong>

Chronological list of all classes:
- Avatar icon color-coded by status
- Class code, subject, status tag
- Date, time range
- Teacher name, room number
- Homework status
- Sorted by date (oldest to newest)

<strong>Status Color Coding:</strong>
- <strong>Present:</strong> Green - Class attended
- <strong>Late:</strong> Yellow/Orange - Arrived late
- <strong>Absent:</strong> Red - Did not attend
- <strong>Scheduled/No Attendance:</strong> Gray - Upcoming or no record

<strong>Key Metrics Explained:</strong>
- <strong>Study Efficiency:</strong> (Present classes / Completed classes) × 100
- <strong>Attendance Rate:</strong> ((Present + Late) / Total classes) × 100
- <strong>Study Progress:</strong> (Studied hours / Total scheduled hours) × 100
- <strong>Completion Rate:</strong> (Completed classes / Total classes) × 100

<strong>Best Practices:</strong>
- Check Overview tab for overall performance
- Monitor attendance rates per course
- Track upcoming hours to plan study time
- Review Timeline for complete history
- Note teacher comments for improvement areas
- Keep homework completion rate high`,
  image: "/manual-images/me.png",
}
        ],
      },
      {
        section: "Leave Request",
        topics: [
          {
  id: 58,
  title: "Request Leave",
  description: `Request to miss a class and schedule a make-up session.

<strong>Leave Quota Cards (Top Section):</strong>
Shows for each enrolled course:
- Course code and subjects
- Progress bar: Used leaves / Max leaves
- Total classes count
- Quota calculation: 2 leaves per 10 classes

<strong>Leave Requests Table:</strong>
- Course/Subject (blue tag with class ID)
- Original Date/Time (scheduled class)
- Preferred Date/Time (make-up request)
- Status tags: Approved (green), Pending (orange), Rejected (red)
- Requested Date, Admin Notes

<strong>Request Leave Button:</strong>
Opens drawer with:

<strong>Leave Policy & Guidelines Alert (Blue):</strong>
- <strong>Leave Entitlements:</strong>
  - 1 class/week: 1 leave/month
  - 2 classes/week: 2 leaves/month
  - 3 classes/week: 3 leaves/month
- <strong>Important Rules:</strong>
  - Notify 1 day in advance (10 AM - 6:30 PM)
  - Emergency leave: 1 hour class may be conducted
  - Leave within 2 hours: Full class may be conducted
  - Private groups: All members notified
  - Group classes: Leave not allowed
  - 3 consecutive leaves: Schedule cancelled/rearranged

<strong>Warning Alerts (When Applicable):</strong>
- <strong>Time Restriction (Yellow):</strong> Submit before 6:30 PM day before class
- <strong>Consecutive Limit (Red):</strong> 2 consecutive taken - must use Pause instead (with "Request Pause Instead" button)
<img src="/manual-images/student-request-leave2.png" alt="Request Leave Form" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Form Fields:</strong>
1. <strong>Select Class to Take Leave:</strong>
   - Shows: Course code, subject, date, time, class ID
   - Disabled if: No quota, too late, 2 consecutive leaves
   - Error messages explain why unavailable
2. <strong>Preferred Make-up Class:</strong>
   - Preferred Date (datepicker, future dates only)
   - Preferred Time (dropdown: 8:00-19:30, 30-min intervals)
3. <strong>Reason for Leave:</strong> Text area (required)
4. Cancel and Submit buttons

<strong>Leave Restrictions:</strong>
- Cannot request after 6:30 PM day before
- Max 2 consecutive leaves (3rd requires pause)
- Subject to quota (2 per 10 classes)
- Must select future class only

<strong>Best Practices:</strong>
- Submit as early as possible
- Check quota before requesting
- Avoid consecutive leaves
- Provide clear reason
- Monitor request status
- Note admin feedback`,
  image: "/manual-images/student-request-leave.png",
},
{
  id: 59,
  title: "Request Pause",
  description: `Request to temporarily pause or take extended break from course.

<strong>Pause Requests Table:</strong>
- Course code (purple tag)
- Pause Type tag (orange): Short Break, Long Break, Pause Course
- Duration (planned time off)
- Resume Date
- Status tags: Approved (green), Pending (orange), Rejected (red)
- Admin Notes

<strong>Request Pause Button:</strong>
Opens drawer with:

<strong>Course Break & Pause Policy Alert (Yellow/Warning):</strong>

<strong>1. Short Break (≤3 weeks):</strong>
- Spot reserved on teacher's schedule

<strong>2. Long Break (>3 weeks):</strong>
- Schedule rearranged
- Same teacher not guaranteed (best effort)
- Classes put on waiting list

<strong>3. Pause Course (≥6 weeks):</strong>
- Enrollment preserved max 365 days from pause
- Teacher and schedule NOT reserved
- Submit at least 3 days before pause begins

<strong>Important Warnings (Orange box):</strong>
- Course expires in 90 days if form not submitted within 15 days after pause
- Course terminates if not resumed on stated date
- Course marked incomplete after 365 days - remaining time forfeited
- All courses non-refundable

<strong>Form Fields:</strong>
1. <strong>Select Course to Pause:</strong> Dropdown of all enrolled courses
2. <strong>Pause Type:</strong> 
   - Short Break (3-8 weeks)
   - Long Break (2-6 months)
   - Pause Course (6+ months)
3. <strong>When Planning to Resume:</strong>
   - 1-3 months
   - 4-6 months
   - 6-9 months
   - 9-12 months
4. <strong>Expected Resume Date:</strong> Datepicker (min 21 days from today)
5. <strong>Reason for Pause:</strong> Text area (required)
6. Cancel and Submit buttons

<strong>Key Differences from Leave:</strong>
- Affects entire course, not single class
- Longer duration (weeks to months)
- Does not reserve teacher/schedule
- Has expiration consequences
- Must plan resume date

<strong>Best Practices:</strong>
- Submit at least 3 days before pause
- Choose appropriate pause type
- Set realistic resume date
- Submit resume form within 15 days after pause starts
- Resume on stated date to avoid termination
- Understand that teacher may change`,
  image: "/manual-images/student-request-pause.png",
}
        ],
      },
      {
        section: "My Billings",
        topics: [
          {
  id: 60,
  title: "My Billings",
  description: `View payment history, receipts, and outstanding balances for all enrolled courses.

<strong>Summary Statistics (4 Cards):</strong>
- <strong>Total Paid:</strong> All payments made (green, ฿)
- <strong>Outstanding Balance:</strong> Remaining amount due (red if > 0, green if paid)
- <strong>Total Transactions:</strong> Number of payment records (blue)
- <strong>Last Payment:</strong> Date of most recent payment (purple)

<strong>Outstanding Balance Alert (Yellow):</strong>
Shows when balance > 0:
- Amount owed in THB
- Message to contact office for payment

<strong>Billing History Table Columns:</strong>

1. <strong>Receipt:</strong> Blue button with receipt number - click to view full receipt PDF
2. <strong>Date:</strong> Payment date (MMM DD, YYYY) with time (HH:MM)
3. <strong>Courses:</strong> 
   - Blue tags with class codes
   - Shows: Price (฿) and total hours per course
   - Multiple courses listed separately
4. <strong>Materials:</strong>
   - Gold tags with item names
   - Shows: Price (฿) and quantity per item
   - N/A if no materials purchased
5. <strong>Discount:</strong> Red text with minus sign (-฿)
6. <strong>VAT:</strong> Tax amount (฿)
7. <strong>Final Price:</strong> Total after discount and VAT (฿)
8. <strong>Amount Paid:</strong> Paid amount in green (฿)
9. <strong>Remaining:</strong> Outstanding balance (red if > 0, green if fully paid)
10. <strong>Payment Method:</strong> Color-coded tags:
    - Cash (green with dollar icon)
    - Credit Card (blue with card icon)
    - Bank Transfer (purple with dollar icon)

<img src="/manual-images/student-billing2.png" alt="Student Billing Modal" style="max-width: 100%; margin: 20px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
<strong>Receipt:</strong>
Click receipt button to open:
- Full receipt details
- Company information
- Itemized breakdown
- Payment summary
- Download/Print options


<strong>Best Practices:</strong>
- Check outstanding balance regularly
- Download receipts for tax purposes
- Review course and material charges
- Note payment methods used
- Contact office if discrepancies found
- Keep receipt numbers for reference`,
  image: "/manual-images/student-billing1.png",
}
        ],
      },
    ],
  },
];